<template>
  <div class="review-page">
    <div v-if="isCommittee && !selectedAgreement" class="review-shell review-shell--landing">
      <AppSectionHero
        :title="text.reviewTitle"
        :subtitle="text.reviewSubtitle"
        :stats="committeeLandingHeroStats"
        :show-refresh="false"
        @stat-click="selectCommitteeLandingStatus"
      />

      <CCard class="border-0 shadow-sm filter-shell h-100">
        <CCardBody class="p-4">
          <div class="toolbar">
            <div class="search-box">
              <span class="search-icon">🔍</span>
              <input
                v-model="committeeLandingFilters.search"
                :placeholder="text.searchPlaceholder"
              >
            </div>
            <div class="toolbar-filters">
              <select v-model="committeeLandingFilters.type" class="filter-select">
                <option value="all">{{ text.allTypes }}</option>
                <option v-for="item in committeeLandingTypeOptions" :key="item" :value="item">{{ item }}</option>
              </select>
            </div>
          </div>
        </CCardBody>
      </CCard>

      <CCard class="border-0 shadow-sm table-shell">
        <CCardBody class="p-4">
          <div class="table-head">
            <div class="table-head__copy">{{ text.showing }} {{ committeeLandingFilteredReviews.length }} {{ text.of }} {{ committeeLandingReviews.length }} {{ text.reviews }}</div>
          </div>

          <div v-if="committeeLandingFilteredReviews.length" class="history-table-wrap">
            <div class="history-table">
              <div class="history-table__header">
                <div>{{ text.columns.agreement }}</div>
                <div>{{ text.columns.type }}</div>
                <div>{{ text.columns.partner }}</div>
                <div>{{ text.columns.status }}</div>
                <div>{{ text.columns.submittedDate }}</div>
                <div>{{ text.columns.actions }}</div>
              </div>

              <div
                v-for="review in committeeLandingFilteredReviews"
                :key="review.id"
                class="history-table__row"
              >
                <div class="history-table__cell history-table__cell--agreement">
                  <div class="history-title">{{ review.agreement }}</div>
                  <div class="history-meta">{{ review.id }}</div>
                  <div class="history-note">
                    {{ committeeLandingStageText(review) }}
                  </div>
                </div>

                <div class="history-table__cell">
                  <span class="history-value">{{ review.type }}</span>
                </div>
                <div class="history-table__cell">
                  <span class="history-value">{{ review.partner }}</span>
                </div>
                <div class="history-table__cell">
                  <span class="decision-pill" :class="`decision-pill--${committeeLandingStatusKey(review)}`">
                    {{ committeeLandingStatusText(review) }}
                  </span>
                </div>
                <div class="history-table__cell history-table__cell--date">
                  <span class="history-mobile-label">{{ text.columns.submittedDate }}</span>
                  <span class="history-date">{{ formatShortDate(review.submittedAt) }}</span>
                </div>
                <div class="history-table__cell history-table__cell--actions">
                  <CButton
                    size="sm"
                    color="secondary"
                    variant="outline"
                    class="history-action-btn"
                    :title="committeeLandingActionLabel(review)"
                    :aria-label="committeeLandingActionLabel(review)"
                    @click.stop="openCommitteeReview(review)"
                  >
                    {{ committeeLandingActionLabel(review) }}
                  </CButton>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <div class="empty-state__title">{{ text.noReviews }}</div>
            <div class="empty-state__copy">{{ text.noReviewsHelp }}</div>
          </div>
        </CCardBody>
      </CCard>
    </div>

    <div v-else-if="selectedAgreement && isCommittee" class="review-shell review-shell--committee">
      <section v-if="selectedAgreement.status !== 'approved'" class="review-header">
        <div>
          <div class="eyebrow">{{ text.committeeWorkflow }}</div>
          <h1>{{ selectedAgreement.title }}</h1>
          <p>
            {{ selectedAgreement.type }} · {{ selectedAgreement.partner }} · Submitted by
            {{ selectedAgreement.createdBy }}
          </p>
        </div>
        <div class="header-meta">
          <div class="meta-chip">{{ selectedAgreement.documentCode }}</div>
          <div class="meta-chip meta-chip--status">{{ stageLabel }}</div>
        </div>
      </section>

      <section v-if="selectedAgreement.status !== 'approved'" class="progress-card">
        <div class="progress-copy">
          <div class="progress-label">{{ text.committeeProgress }}</div>
          <div class="progress-stage">{{ stageLabel }}</div>
        </div>
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: progress + '%' }" />
        </div>
        <div class="progress-value">{{ progress }}%</div>
      </section>

      <CCard class="border-0 shadow-sm review-card review-card--full">
        <CCardBody class="p-4 p-lg-5">
          <div class="card-heading">
            <div>
              <div class="section-title">{{ text.committeeWorkspace }}</div>
              <div class="section-subtitle">{{ text.committeeWorkspaceSubtitle }}</div>
            </div>
          </div>

          <div class="committee-layout committee-layout--synced">
            <div class="committee-main">
              <div class="info-panel">
                <div class="info-title">{{ text.reviewSummary }}</div>
                <div class="detail-grid detail-grid--wide">
                  <div class="detail-row"><span>{{ text.fields.document }}</span><strong>{{ selectedAgreement.fileLabel }}</strong></div>
                  <div class="detail-row"><span>{{ text.fields.submittedOn }}</span><strong>{{ formatDate(selectedAgreement.submittedAt) }}</strong></div>
                  <div class="detail-row"><span>{{ text.fields.partner }}</span><strong>{{ selectedAgreement.partner }}</strong></div>
                  <div class="detail-row"><span>{{ text.fields.country }}</span><strong>{{ selectedAgreement.country || text.notSpecified }}</strong></div>
                  <div class="detail-row"><span>{{ text.fields.currentVersion }}</span><strong>v{{ latestVersionNumber }}</strong></div>
                  <div class="detail-row"><span>{{ text.fields.totalVersions }}</span><strong>{{ versions.length }}</strong></div>
                  <div class="detail-row"><span>{{ text.fields.dueDate }}</span><strong>{{ formatDate(selectedAgreement.dueDate) }}</strong></div>
                  <div class="detail-row"><span>{{ text.fields.daysLeft }}</span><strong>{{ selectedAgreement.daysLeft }}</strong></div>
                  <div class="detail-row detail-row--wide"><span>{{ text.fields.summary }}</span><strong>{{ selectedAgreement.summary || text.noSummary }}</strong></div>
                </div>
              </div>

              <div class="preview-panel">
                <div class="panel-title">{{ text.previewAndDetail }}</div>
                <div class="pdf-sheet">
                  <div class="pdf-topline">
                    <span>{{ previewAttachmentLabel }}</span>
                    <span>{{ selectedAgreement.documentCode }}</span>
                  </div>
                  <AgreementDocumentHeader
                    class="pdf-sheet__branding"
                    :title="selectedAgreement.title"
                    :partner="selectedAgreement.partner"
                    :partner-logo="selectedAgreement.partnerLogo"
                    :agreement-type="selectedAgreement.type"
                    :country="selectedAgreement.country"
                    title-label="Agreement Title"
                    compact
                    :show-meta="true"
                  />
                  <div v-if="previewLoading" class="pdf-empty-state">
                    {{ text.loadingDraft }}
                  </div>
                  <div v-else-if="displayDraftPreviewSections.length" class="draft-preview-stack">
                    <div
                      v-for="section in displayDraftPreviewSections"
                      :id="committeeSectionId(section.key)"
                      :key="section.key"
                      class="draft-preview-card"
                      :class="draftPreviewCardClass(section)"
                    >
                      <div class="draft-preview-card__header">
                        <div class="draft-preview-card__title">{{ section.title }}</div>
                        <span v-if="sectionChangeThreads(section).length" class="draft-preview-card__badge">{{ sectionChangeLabel(section) }}</span>
                      </div>
                      <div v-if="sectionChangeThreads(section).length" class="draft-preview-card__markers">
                        <button
                          v-for="thread in sectionChangeThreads(section)"
                          :key="thread.threadId"
                          type="button"
                          class="draft-preview-card__marker"
                          :class="{ 'draft-preview-card__marker--resolved': thread.status === 'resolved' }"
                          @click.stop="openCommitteeThreadContext(thread)"
                        >
                          {{ thread.root.copy || thread.root.content }}
                        </button>
                      </div>
                      <div
                        class="draft-preview-card__copy"
                        @mouseup="captureCommitteeTextAnchor(section, $event)"
                        @keyup="captureCommitteeTextAnchor(section, $event)"
                        v-html="renderHighlightedSectionContent(section)"
                      ></div>
                    </div>
                  </div>
                  <div v-else-if="previewUrl" class="pdf-empty-state">
                    <strong>{{ text.previewNotReady }}</strong>
                    <p>{{ previewAttachmentLabel }}</p>
                    <a :href="previewUrl" target="_blank" rel="noopener">{{ text.openDocumentFile }}</a>
                  </div>
                  <div v-else class="pdf-empty-state">
                    <strong>{{ text.noWordDocument }}</strong>
                    <p>{{ previewError || text.noWordDocumentDetail }}</p>
                  </div>
                </div>
              </div>

            </div>

            <div class="committee-side">
              <div class="summary-panel version-panel version-panel--compact">
                <div class="panel-title">{{ text.versionHistory }}</div>
                <div v-if="versions.length" class="version-list version-list--compact">
                  <div v-for="version in versions.slice(0, 3)" :key="version.versionNumber" class="version-item version-item--compact">
                    <strong>v{{ version.versionNumber }}</strong>
                    <span>{{ formatDateTime(version.createdAt) }}</span>
                  </div>
                </div>
                <div v-else class="empty-assignment">{{ text.noSavedVersions }}</div>
              </div>

              <div class="summary-panel">
                <div class="panel-title">{{ text.readComments }}</div>
                <div class="section-subtitle">{{ text.readCommentsHelp }}</div>
                <div v-if="commentThreads.length" class="committee-comments committee-comments--thread">
                  <div v-for="thread in commentThreads" :key="thread.threadId" class="comment-thread-shell">
                    <div
                      class="comment-card comment-card--interactive"
                      :class="{
                        'comment-card--committee': thread.root.role === 'Committee',
                        'comment-card--user': thread.root.role === 'User',
                        'comment-card--active': replyTargetThreadId === thread.threadId,
                        'comment-card--resolved': thread.status === 'resolved',
                        'comment-card--unresolved': thread.status !== 'resolved'
                      }"
                      role="button"
                      tabindex="0"
                      @click="openCommitteeThreadContext(thread)"
                      @keydown.enter.prevent="openCommitteeThreadContext(thread)"
                      @keydown.space.prevent="openCommitteeThreadContext(thread)"
                    >
                      <div class="comment-head">
                        <strong>{{ thread.root.author }}</strong>
                        <span>{{ formatDate(thread.root.date) }}</span>
                      </div>
                      <div class="comment-role">{{ thread.root.roleLabel || thread.root.role }} · {{ thread.root.articleTitle || thread.root.section || text.general }} · {{ text.page }} {{ thread.root.page || '-' }} · v{{ thread.root.versionNumber || 1 }}</div>
                      <div
                        v-if="isCommitteeThread(thread)"
                        class="comment-change-target"
                        :class="{ 'comment-change-target--resolved': thread.status === 'resolved' }"
                      >
                        <span>{{ thread.status === 'resolved' ? text.changeResolved : text.changeRequested }}</span>
                        <strong>{{ commentTargetTitle(thread.root) }}</strong>
                        <em v-if="commentAnchorText(thread.root)">{{ commentAnchorText(thread.root) }}</em>
                      </div>
                      <div class="comment-copy">{{ thread.root.content }}</div>
                      <div class="comment-thread-actions">
                        <button type="button" class="comment-thread-link" @click.stop="prepareCommitteeReply(thread, thread.root)">{{ text.reply }}</button>
                        <button v-if="thread.status !== 'resolved'" type="button" class="comment-thread-link" @click.stop="resolveCommitteeThread(thread)">{{ text.resolve }}</button>
                        <button v-else type="button" class="comment-thread-link comment-thread-link--undo" @click.stop="undoResolveCommitteeThread(thread)">{{ text.undoResolve }}</button>
                      </div>
                    </div>
                    <div v-if="replyTargetThreadId === thread.threadId" class="inline-reply-indicator">
                      <span class="inline-reply-indicator__arrow">↳</span>
                      <span>{{ text.replyingBelow }}</span>
                      <button type="button" class="comment-thread-link" @click="clearCommitteeReplyTarget">{{ text.clear }}</button>
                    </div>
                    <div v-if="replyTargetThreadId === thread.threadId" class="form-stack form-stack--composer form-stack--inline">
                      <div class="panel-title panel-title--composer">Reply To Comment</div>
                      <label class="decision-label">
                        {{ text.articleName }}
                        <select v-model="committeeForm.articleKey" class="decision-select" @change="applyCommitteeArticleSelection">
                          <option v-for="item in articleOptions" :key="item.key" :value="item.key">{{ item.title }}</option>
                        </select>
                      </label>
                      <div class="article-reference">
                        Commenting on: {{ currentCommitteeArticleTitle() }}
                      </div>
                      <div v-if="committeeForm.anchor && committeeForm.anchor.text" class="selected-anchor-chip">
                        <span>{{ text.selectedTarget }}</span>
                        <strong>{{ committeeForm.anchor.text }}</strong>
                        <button type="button" @click="clearCommitteeAnchor">{{ text.clear }}</button>
                      </div>
                      <label class="decision-label" for="committee-note">
                        Comments
                      </label>
                      <textarea
                        id="committee-note"
                        v-model="committeeForm.comments"
                        class="decision-note"
                        rows="6"
                        placeholder="Write a committee reply, question, or requested revision."
                      />
                      <div class="decision-actions">
                        <CButton color="primary" :disabled="committeeCommentSubmitting" @click="saveCommitteeDraft">Post Comment</CButton>
                        <CButton color="secondary" variant="outline" @click="closeCommitteeComposer">Cancel</CButton>
                      </div>
                    </div>
                    <div v-if="thread.replies.length" class="comment-thread-replies">
                      <div
                        v-for="reply in thread.replies"
                        :key="reply.commentId || reply.id"
                        class="comment-card comment-card--reply"
                        :class="{
                          'comment-card--committee': reply.role === 'Committee',
                          'comment-card--user': reply.role === 'User'
                        }"
                      >
                        <div class="comment-head">
                          <strong>{{ reply.author }}</strong>
                          <span>{{ formatDate(reply.date) }}</span>
                        </div>
                        <div class="comment-role">{{ reply.roleLabel || reply.role }} · Page {{ reply.page || '-' }} · v{{ reply.versionNumber || 1 }}</div>
                        <div class="comment-copy">{{ reply.content }}</div>
                        <div class="comment-thread-actions comment-thread-actions--reply">
                          <button type="button" class="comment-thread-link" @click.stop="prepareCommitteeReply(thread, reply)">{{ text.reply }}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-assignment">No comments saved yet.</div>
                <div v-if="!showCommitteeComposer && !replyTargetThreadId" class="composer-launch">
                  <button type="button" class="composer-launch__btn" @click="openCommitteeComposer">
                    Add New Comment
                  </button>
                </div>
                <div v-if="showCommitteeComposer && !replyTargetThreadId" class="form-stack form-stack--composer">
                  <div class="panel-title panel-title--composer">Add Committee Comment</div>
                  <label class="decision-label">
                    {{ text.articleName }}
                    <select v-model="committeeForm.articleKey" class="decision-select" @change="applyCommitteeArticleSelection">
                      <option v-for="item in articleOptions" :key="item.key" :value="item.key">{{ item.title }}</option>
                    </select>
                  </label>
                  <label class="decision-label" for="committee-note">
                    Comments
                  </label>
                  <div v-if="committeeForm.anchor && committeeForm.anchor.text" class="selected-anchor-chip">
                    <span>{{ text.selectedTarget }}</span>
                    <strong>{{ committeeForm.anchor.text }}</strong>
                    <button type="button" @click="clearCommitteeAnchor">{{ text.clear }}</button>
                  </div>
                  <textarea
                    id="committee-note"
                    v-model="committeeForm.comments"
                    class="decision-note"
                    rows="8"
                    placeholder="Write a committee comment, question, or requested revision."
                  />
                  <div class="decision-actions">
                    <CButton color="primary" :disabled="committeeCommentSubmitting" @click="saveCommitteeDraft">Post Comment</CButton>
                    <CButton color="secondary" variant="outline" @click="closeCommitteeComposer">Cancel</CButton>
                  </div>
                </div>
              </div>

              <div v-if="showCommitteeFinalPanel" class="summary-panel committee-complete-review" :class="{ 'committee-complete-review--blocked': !canSubmitCommitteeFinal }">
                <div class="panel-title">{{ canSubmitCommitteeFinal ? text.finalReviewTitle : committeeSubmitBlockedTitle }}</div>
                <div class="section-subtitle">{{ canSubmitCommitteeFinal ? text.finalReviewHelp : committeeSubmitBlockedHelp }}</div>
                <div class="detail-grid compact committee-submit-summary">
                  <div class="detail-row"><span>{{ text.totalComments }}</span><strong>{{ commentCount }}</strong></div>
                  <div v-if="commentThreads.length" class="detail-row"><span>{{ text.openThreads }}</span><strong>{{ unresolvedCommentThreadCount }}</strong></div>
                  <div class="detail-row"><span>{{ text.currentProgress }}</span><strong>{{ progress }}%</strong></div>
                  <div class="detail-row"><span>{{ text.currentStage }}</span><strong>{{ stageLabel }}</strong></div>
                </div>
                <div class="form-stack form-stack--final-simple">
                  <div class="decision-actions decision-actions--stacked">
                    <button v-if="canSubmitCommitteeFinal" type="button" class="composer-launch__btn" @click="submitCommitteeReview">
                      {{ committeeSubmitActionLabel }}
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div class="footer-actions footer-actions--spread">
            <div class="footer-actions__left">
              <CButton color="secondary" variant="outline" class="mr-2" @click="goBack">Back to Dashboard</CButton>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </div>

    <div v-else-if="selectedAgreement" class="review-shell">
      <section v-if="selectedAgreement.status !== 'approved'" class="review-header">
        <div>
          <div class="eyebrow">Admin Review Workflow</div>
          <h1>{{ selectedAgreement.title }}</h1>
          <p>
            {{ selectedAgreement.type }} · {{ selectedAgreement.partner }} · Submitted by
            {{ selectedAgreement.createdBy }}
          </p>
        </div>

        <div class="header-meta">
          <div class="meta-chip">MOU-{{ selectedAgreement.id }}</div>
          <div class="meta-chip meta-chip--status">{{ statusLabel }}</div>
        </div>
      </section>

      <section v-if="selectedAgreement.status !== 'approved'" class="progress-card">
        <div class="progress-copy">
          <div class="progress-label">Current progress</div>
          <div class="progress-stage">{{ stageLabel }}</div>
        </div>
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: progress + '%' }" />
        </div>
        <div class="progress-value">{{ progress }}%</div>
      </section>

      <section v-if="selectedAgreement.status !== 'approved'" class="stepper-card">
        <div
          v-for="step in steps"
          :key="step.id"
          class="step-pill"
          :class="{
            'step-pill--active': currentStep === step.id,
            'step-pill--done': progress >= step.id * 25 && selectedAgreement.status !== 'draft'
          }"
        >
          <span class="step-number">{{ step.id }}</span>
          <span class="step-text">{{ step.title }}</span>
        </div>
      </section>

      <CCard class="border-0 shadow-sm review-card review-card--full">
        <CCardBody class="p-4 p-lg-5">
          <div v-if="selectedAgreement.status !== 'approved'" class="card-heading">
            <div>
              <div class="section-title">{{ activeStep.title }}</div>
              <div class="section-subtitle">{{ activeStep.description }}</div>
            </div>
          </div>

          <div v-if="selectedAgreement.status === 'approved'" class="step-page">
            <div class="preview-layout preview-layout--full">
              <div class="preview-panel">
                <div class="panel-title">Approved Document Preview</div>
                <div class="pdf-sheet">
                  <div class="pdf-topline">
                    <span>{{ previewAttachmentLabel }}</span>
                    <span>{{ selectedAgreement.documentCode }}</span>
                  </div>
                  <AgreementDocumentHeader
                    class="pdf-sheet__branding"
                    :title="selectedAgreement.title"
                    :partner="selectedAgreement.partner"
                    :partner-logo="selectedAgreement.partnerLogo"
                    :agreement-type="selectedAgreement.type"
                    :country="selectedAgreement.country"
                    title-label="Agreement Title"
                    compact
                    :show-meta="true"
                  />
                  <div v-if="previewLoading" class="pdf-empty-state">
                    {{ text.loadingDraft }}
                  </div>
                  <div v-else-if="displayDraftPreviewSections.length" class="draft-preview-stack">
                    <div
                      v-for="section in displayDraftPreviewSections"
                      :id="committeeSectionId(section.key)"
                      :key="section.key"
                      class="draft-preview-card"
                      :class="draftPreviewCardClass(section)"
                    >
                      <div class="draft-preview-card__header">
                        <div class="draft-preview-card__title">{{ section.title }}</div>
                        <span v-if="sectionChangeThreads(section).length" class="draft-preview-card__badge">{{ sectionChangeLabel(section) }}</span>
                      </div>
                      <div v-if="sectionChangeThreads(section).length" class="draft-preview-card__markers">
                        <button
                          v-for="thread in sectionChangeThreads(section)"
                          :key="thread.threadId"
                          type="button"
                          class="draft-preview-card__marker"
                          :class="{ 'draft-preview-card__marker--resolved': thread.status === 'resolved' }"
                          @click.stop="openCommitteeThreadContext(thread)"
                        >
                          {{ thread.root.copy || thread.root.content }}
                        </button>
                      </div>
                      <div
                        class="draft-preview-card__copy"
                        @mouseup="captureCommitteeTextAnchor(section, $event)"
                        @keyup="captureCommitteeTextAnchor(section, $event)"
                        v-html="renderHighlightedSectionContent(section)"
                      ></div>
                    </div>
                  </div>
                  <div v-else-if="previewUrl" class="pdf-empty-state">
                    <strong>In-page preview is not ready</strong>
                    <p>{{ previewAttachmentLabel }}</p>
                    <a :href="previewUrl" target="_blank" rel="noopener">Open document file</a>
                  </div>
                  <div v-else class="pdf-empty-state">
                    <strong>No Word document available</strong>
                    <p>{{ previewError || 'No attached DOC or DOCX was found, and the agreement draft content is also empty.' }}</p>
                  </div>
                </div>
              </div>

              <div class="decision-side decision-side--stacked">
                <div class="summary-panel">
                  <div class="panel-title">Agreement Details</div>
                  <div class="detail-grid compact">
                    <div class="detail-row"><span>Document ID</span><strong>{{ selectedAgreement.documentCode || `MOU-${selectedAgreement.id}` }}</strong></div>
                    <div class="detail-row"><span>Title</span><strong>{{ selectedAgreement.title }}</strong></div>
                    <div class="detail-row"><span>Created by</span><strong>{{ selectedAgreement.createdBy || '-' }}</strong></div>
                    <div class="detail-row"><span>Submitted on</span><strong>{{ formatDate(selectedAgreement.submittedAt) }}</strong></div>
                    <div class="detail-row"><span>Partner</span><strong>{{ selectedAgreement.partner || '-' }}</strong></div>
                    <div class="detail-row"><span>Type</span><strong>{{ selectedAgreement.type || '-' }}</strong></div>
                    <div class="detail-row"><span>Department</span><strong>{{ selectedAgreement.department || '-' }}</strong></div>
                    <div class="detail-row"><span>Status</span><strong>{{ statusLabel }}</strong></div>
                    <div class="detail-row"><span>Committee members</span><strong>{{ committeeCount }}</strong></div>
                    <div class="detail-row"><span>Total comments</span><strong>{{ commentCount }}</strong></div>
                    <div class="detail-row detail-row--wide"><span>Summary</span><strong>{{ selectedAgreement.summary || 'No summary provided.' }}</strong></div>
                  </div>
                </div>

                <div class="summary-panel">
                  <div class="panel-title">Assigned Committee</div>
                  <div v-if="committeeMembers.length" class="assigned-list">
                    <div v-for="name in committeeMembers" :key="name" class="assigned-person">
                      <strong>{{ name }}</strong>
                      <span>Committee member</span>
                    </div>
                  </div>
                  <div v-else class="empty-assignment">No committee members were recorded for this agreement.</div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="currentStep === 1" class="step-page step-page--intro">
            <div class="hero-block">
              <div class="info-title">Draft received from user</div>
              <div class="info-copy">
                The user uploaded the MOU draft. The workflow starts here before the document moves straight into committee review.
              </div>
            </div>
            <div class="detail-grid detail-grid--wide">
              <div class="detail-row"><span>Document</span><strong>{{ selectedAgreement.fileLabel }}</strong></div>
              <div class="detail-row"><span>Submitted on</span><strong>{{ formatDate(selectedAgreement.submittedAt) }}</strong></div>
              <div class="detail-row"><span>Created by</span><strong>{{ selectedAgreement.createdBy }}</strong></div>
              <div class="detail-row"><span>Partner</span><strong>{{ selectedAgreement.partner }}</strong></div>
              <div class="detail-row"><span>Department</span><strong>{{ selectedAgreement.department }}</strong></div>
              <div class="detail-row"><span>Type</span><strong>{{ selectedAgreement.type }}</strong></div>
              <div class="detail-row detail-row--wide"><span>Summary</span><strong>{{ selectedAgreement.summary }}</strong></div>
            </div>
          </div>

          <div v-else-if="currentStep === 2" class="step-page">
            <div class="full-page-layout">
              <div class="page-main">
                <div class="preview-panel">
                  <div class="panel-title">Document Preview</div>
                  <div class="pdf-sheet">
                    <div class="pdf-topline">
                      <span>{{ previewAttachmentLabel }}</span>
                      <span>{{ selectedAgreement.documentCode }}</span>
                    </div>
                    <AgreementDocumentHeader
                      class="pdf-sheet__branding"
                      :title="selectedAgreement.title"
                      :partner="selectedAgreement.partner"
                      :partner-logo="selectedAgreement.partnerLogo"
                      :agreement-type="selectedAgreement.type"
                      :country="selectedAgreement.country"
                      title-label="Agreement Title"
                      compact
                      :show-meta="true"
                    />
                    <div v-if="previewLoading" class="pdf-empty-state">
                      {{ text.loadingDraft }}
                    </div>
                    <div v-else-if="displayDraftPreviewSections.length" class="draft-preview-stack">
                      <div
                        v-for="section in displayDraftPreviewSections"
                        :id="committeeSectionId(section.key)"
                        :key="section.key"
                        class="draft-preview-card"
                        :class="draftPreviewCardClass(section)"
                      >
                        <div class="draft-preview-card__header">
                          <div class="draft-preview-card__title">{{ section.title }}</div>
                          <span v-if="sectionChangeThreads(section).length" class="draft-preview-card__badge">{{ sectionChangeLabel(section) }}</span>
                        </div>
                        <div v-if="sectionChangeThreads(section).length" class="draft-preview-card__markers">
                          <button
                            v-for="thread in sectionChangeThreads(section)"
                            :key="thread.threadId"
                            type="button"
                            class="draft-preview-card__marker"
                            :class="{ 'draft-preview-card__marker--resolved': thread.status === 'resolved' }"
                            @click.stop="openCommitteeThreadContext(thread)"
                          >
                            {{ thread.root.copy || thread.root.content }}
                          </button>
                        </div>
                        <div
                          class="draft-preview-card__copy"
                          @mouseup="captureCommitteeTextAnchor(section, $event)"
                          @keyup="captureCommitteeTextAnchor(section, $event)"
                          v-html="renderHighlightedSectionContent(section)"
                        ></div>
                      </div>
                    </div>
                    <div v-else-if="previewUrl" class="pdf-empty-state">
                      <strong>In-page preview is not ready</strong>
                      <p>{{ previewAttachmentLabel }}</p>
                      <a :href="previewUrl" target="_blank" rel="noopener">Open document file</a>
                    </div>
                    <div v-else class="pdf-empty-state">
                      <strong>No Word document available</strong>
                      <p>{{ previewError || 'No attached DOC or DOCX was found, and the agreement draft content is also empty.' }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="page-side">
                <div class="summary-panel">
                  <div class="panel-title">Committee Routing Details</div>
                  <div class="detail-grid compact">
                    <div class="detail-row"><span>Document</span><strong>{{ selectedAgreement.documentCode || `MOU-${selectedAgreement.id}` }}</strong></div>
                    <div class="detail-row"><span>Submitted by</span><strong>{{ selectedAgreement.createdBy }}</strong></div>
                    <div class="detail-row"><span>Partner</span><strong>{{ selectedAgreement.partner }}</strong></div>
                    <div class="detail-row"><span>Type</span><strong>{{ selectedAgreement.type }}</strong></div>
                    <div class="detail-row"><span>Department</span><strong>{{ selectedAgreement.department }}</strong></div>
                    <div class="detail-row"><span>Submitted on</span><strong>{{ formatDate(selectedAgreement.submittedAt) }}</strong></div>
                    <div class="detail-row"><span>Committee queue members</span><strong>{{ selectedReviewerNames.length }}</strong></div>
                    <div class="detail-row detail-row--wide"><span>Summary</span><strong>{{ selectedAgreement.summary || 'No summary provided.' }}</strong></div>
                  </div>
                </div>

                <div class="summary-panel">
                  <div class="panel-title">Committee Review Starts Automatically</div>
                  <div class="section-subtitle">Admin does not manually choose committee members here. The submitted document is sent directly to the shared committee review team.</div>
                  <div class="reviewer-grid">
                    <div
                      v-for="reviewer in selectedReviewers"
                      :key="reviewer.id"
                      class="reviewer-option reviewer-option--static"
                    >
                      <div class="reviewer-copy">
                        <strong>{{ reviewer.name }}</strong>
                        <span>{{ reviewer.role }}</span>
                        <small>{{ reviewer.department }}</small>
                      </div>
                    </div>
                  </div>
                  <div class="assigned-list">
                    <div v-if="selectedReviewers.length">
                      <div v-for="reviewer in selectedReviewers" :key="reviewer.id" class="assigned-person">
                        <strong>{{ reviewer.name }}</strong>
                        <span>{{ reviewer.role }}</span>
                      </div>
                    </div>
                    <div v-else class="empty-assignment">Committee queue members will appear here once the document sync completes.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="currentStep === 3" class="step-page">
            <div class="assignment-banner">
              <div>
                <div class="panel-title">Committee Queue Details</div>
                <div class="section-subtitle">This document stays visible to the committee review queue throughout the review stage.</div>
              </div>
              <div class="assignment-tags">
                <span v-for="name in selectedReviewerNames" :key="name" class="assignment-tag">{{ name }}</span>
              </div>
            </div>

            <div class="preview-layout preview-layout--full">
              <div class="preview-panel">
                <div class="panel-title">MOU Draft Preview and Detail</div>
                <div class="pdf-sheet">
                  <div class="pdf-topline">
                    <span>{{ previewAttachmentLabel }}</span>
                    <span>{{ selectedAgreement.documentCode }}</span>
                  </div>
                  <AgreementDocumentHeader
                    class="pdf-sheet__branding"
                    :title="selectedAgreement.title"
                    :partner="selectedAgreement.partner"
                    :partner-logo="selectedAgreement.partnerLogo"
                    :agreement-type="selectedAgreement.type"
                    :country="selectedAgreement.country"
                    title-label="Agreement Title"
                    compact
                    :show-meta="true"
                  />
                  <div v-if="activeComment" class="pdf-reference">
                    Looking at Page {{ activeComment.page || '-' }} · {{ activeComment.section }}
                  </div>
                  <div v-if="previewLoading" class="pdf-empty-state">
                    {{ text.loadingDraft }}
                  </div>
                  <div v-else-if="displayDraftPreviewSections.length" class="draft-preview-stack">
                    <div
                      v-for="section in displayDraftPreviewSections"
                      :id="committeeSectionId(section.key)"
                      :key="section.key"
                      class="draft-preview-card"
                      :class="draftPreviewCardClass(section)"
                    >
                      <div class="draft-preview-card__header">
                        <div class="draft-preview-card__title">{{ section.title }}</div>
                        <span v-if="sectionChangeThreads(section).length" class="draft-preview-card__badge">{{ sectionChangeLabel(section) }}</span>
                      </div>
                      <div v-if="sectionChangeThreads(section).length" class="draft-preview-card__markers">
                        <button
                          v-for="thread in sectionChangeThreads(section)"
                          :key="thread.threadId"
                          type="button"
                          class="draft-preview-card__marker"
                          :class="{ 'draft-preview-card__marker--resolved': thread.status === 'resolved' }"
                          @click.stop="openCommitteeThreadContext(thread)"
                        >
                          {{ thread.root.copy || thread.root.content }}
                        </button>
                      </div>
                      <div
                        class="draft-preview-card__copy"
                        @mouseup="captureCommitteeTextAnchor(section, $event)"
                        @keyup="captureCommitteeTextAnchor(section, $event)"
                        v-html="renderHighlightedSectionContent(section)"
                      ></div>
                    </div>
                  </div>
                  <div v-else-if="previewUrl" class="pdf-empty-state">
                    <strong>In-page preview is not ready</strong>
                    <p>{{ previewAttachmentLabel }}</p>
                    <a :href="previewUrl" target="_blank" rel="noopener">Open document file</a>
                  </div>
                  <div v-else class="pdf-empty-state">
                    <strong>No Word document available</strong>
                    <p>{{ previewError || 'No attached DOC or DOCX was found, and the agreement draft content is also empty.' }}</p>
                  </div>
                </div>
              </div>

              <div class="comment-panel">
                <div class="panel-title">Committee and User Comments</div>
                <div
                  v-for="comment in selectedAgreement.comments"
                  :key="comment.id"
                  class="comment-card"
                  :class="{ 'comment-card--active': activeCommentId === comment.id }"
                  @click="selectComment(comment.id)"
                >
                  <div class="comment-head">
                    <strong>{{ comment.author }}</strong>
                    <span>{{ comment.time }}</span>
                  </div>
                  <div class="comment-role">{{ comment.roleLabel || comment.role }} · {{ comment.articleTitle || comment.section || text.general }} · {{ text.page }} {{ comment.page || '-' }}</div>
                  <div
                    v-if="isCommitteeComment(comment)"
                    class="comment-change-target"
                    :class="{ 'comment-change-target--resolved': comment.status === 'resolved' }"
                  >
                    <span>{{ comment.status === 'resolved' ? text.changeResolved : text.changeRequested }}</span>
                    <strong>{{ commentTargetTitle(comment) }}</strong>
                    <em v-if="commentAnchorText(comment)">{{ commentAnchorText(comment) }}</em>
                  </div>
                  <div class="comment-copy">{{ comment.copy }}</div>
                </div>

                <div class="live-history-divider" />
                <div class="panel-title panel-title--sub">{{ text.liveReviewHistory }}</div>
                <div class="section-subtitle">{{ text.liveReviewHistoryHelp }}</div>
                <div v-if="adminLiveReviewTimeline.length" class="live-history-list">
                  <div
                    v-for="item in adminLiveReviewTimeline"
                    :key="item.id"
                    class="live-history-item"
                    :class="`live-history-item--${item.type}`"
                  >
                    <div class="live-history-dot" />
                    <div class="live-history-copy">
                      <div class="live-history-head">
                        <strong>{{ item.title }}</strong>
                        <span>{{ formatDateTime(item.timestamp) }}</span>
                      </div>
                      <div class="live-history-meta">{{ item.meta }}</div>
                      <div v-if="item.summary" class="live-history-summary">{{ item.summary }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-assignment">{{ text.noLiveReviewHistory }}</div>
              </div>
            </div>
          </div>

          <div v-else class="step-page">
            <div class="assignment-banner">
              <div>
                <div class="panel-title">Committee Queue Details</div>
                <div class="section-subtitle">Use the committee queue participation, comments, and document summary before making the final decision.</div>
              </div>
              <div class="assignment-tags">
                <span v-for="name in selectedReviewerNames" :key="name" class="assignment-tag">{{ name }}</span>
              </div>
            </div>

            <div class="decision-layout">
              <div class="decision-main">
                <div class="info-panel">
                  <div class="info-title">Final admin decision</div>
                  <div class="info-copy">
                    Approve the MOU to complete the workflow or reject it to restart from the first step.
                  </div>
                </div>
                <div class="preview-panel">
                  <div class="panel-title">MOU Draft Preview and Detail</div>
                  <div class="pdf-sheet">
                    <div class="pdf-topline">
                      <span>{{ previewAttachmentLabel }}</span>
                      <span>{{ selectedAgreement.documentCode }}</span>
                    </div>
                    <AgreementDocumentHeader
                      class="pdf-sheet__branding"
                      :title="selectedAgreement.title"
                      :partner="selectedAgreement.partner"
                      :partner-logo="selectedAgreement.partnerLogo"
                      :agreement-type="selectedAgreement.type"
                      :country="selectedAgreement.country"
                      title-label="Agreement Title"
                      compact
                      :show-meta="true"
                    />
                    <div v-if="activeComment" class="pdf-reference">
                      Looking at Page {{ activeComment.page || '-' }} · {{ activeComment.section }}
                    </div>
                    <div v-if="previewLoading" class="pdf-empty-state">
                      {{ text.loadingDraft }}
                    </div>
                    <div v-else-if="displayDraftPreviewSections.length" class="draft-preview-stack">
                      <div
                        v-for="section in displayDraftPreviewSections"
                        :id="committeeSectionId(section.key)"
                        :key="section.key"
                        class="draft-preview-card"
                        :class="draftPreviewCardClass(section)"
                      >
                        <div class="draft-preview-card__header">
                          <div class="draft-preview-card__title">{{ section.title }}</div>
                          <span v-if="sectionChangeThreads(section).length" class="draft-preview-card__badge">{{ sectionChangeLabel(section) }}</span>
                        </div>
                        <div v-if="sectionChangeThreads(section).length" class="draft-preview-card__markers">
                          <button
                            v-for="thread in sectionChangeThreads(section)"
                            :key="thread.threadId"
                            type="button"
                            class="draft-preview-card__marker"
                            :class="{ 'draft-preview-card__marker--resolved': thread.status === 'resolved' }"
                            @click.stop="openCommitteeThreadContext(thread)"
                          >
                            {{ thread.root.copy || thread.root.content }}
                          </button>
                        </div>
                        <div
                          class="draft-preview-card__copy"
                          @mouseup="captureCommitteeTextAnchor(section, $event)"
                          @keyup="captureCommitteeTextAnchor(section, $event)"
                          v-html="renderHighlightedSectionContent(section)"
                        ></div>
                      </div>
                    </div>
                    <div v-else-if="previewUrl" class="pdf-empty-state">
                      <strong>In-page preview is not ready</strong>
                      <p>{{ previewAttachmentLabel }}</p>
                      <a :href="previewUrl" target="_blank" rel="noopener">Open document file</a>
                    </div>
                    <div v-else class="pdf-empty-state">
                      <strong>No Word document available</strong>
                      <p>{{ previewError || 'No attached DOC or DOCX was found, and the agreement draft content is also empty.' }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="decision-side">
                <div class="summary-panel">
                  <div class="panel-title">Decision Summary</div>
                  <div class="detail-grid compact">
                    <div class="detail-row"><span>Committee queue members</span><strong>{{ selectedReviewerNames.length }}</strong></div>
                    <div class="detail-row"><span>Total comments</span><strong>{{ selectedAgreement.comments.length }}</strong></div>
                    <div class="detail-row"><span>Submitted by</span><strong>{{ selectedAgreement.createdBy }}</strong></div>
                    <div class="detail-row"><span>Status</span><strong>{{ statusLabel }}</strong></div>
                  </div>
                </div>
                <div class="summary-panel">
                  <div class="panel-title">Committee and User Comments</div>
                  <div v-if="selectedAgreement.comments && selectedAgreement.comments.length" class="committee-comments committee-comments--thread">
                    <div
                      v-for="comment in selectedAgreement.comments"
                      :key="comment.id"
                      class="comment-card"
                      :class="{ 'comment-card--active': activeCommentId === comment.id }"
                      @click="selectComment(comment.id)"
                    >
                      <div class="comment-head">
                        <strong>{{ comment.author }}</strong>
                        <span>{{ comment.time }}</span>
                      </div>
                      <div class="comment-role">{{ comment.roleLabel || comment.role }} · {{ comment.articleTitle || comment.section || text.general }} · {{ text.page }} {{ comment.page || '-' }}</div>
                      <div
                        v-if="isCommitteeComment(comment)"
                        class="comment-change-target"
                        :class="{ 'comment-change-target--resolved': comment.status === 'resolved' }"
                      >
                        <span>{{ comment.status === 'resolved' ? text.changeResolved : text.changeRequested }}</span>
                        <strong>{{ commentTargetTitle(comment) }}</strong>
                        <em v-if="commentAnchorText(comment)">{{ commentAnchorText(comment) }}</em>
                      </div>
                      <div class="comment-copy">{{ comment.copy }}</div>
                    </div>
                  </div>
                  <div v-else class="empty-assignment">{{ localeCode === 'th' ? 'ไม่มีความคิดเห็นสำหรับข้อตกลงนี้' : 'No comments were recorded for this agreement.' }}</div>
                </div>
                <div class="summary-panel">
                  <div class="panel-title">Final Admin Decision</div>
                  <div class="decision-choice-grid">
                    <button
                      type="button"
                      class="decision-choice decision-choice--approve"
                      :class="{ 'decision-choice--selected': selectedDecision === 'approve' }"
                      @click="chooseDecision('approve')"
                    >
                      <strong>Approve</strong>
                    </button>
                    <button
                      type="button"
                      class="decision-choice decision-choice--reject"
                      :class="{ 'decision-choice--selected': selectedDecision === 'reject' }"
                      @click="chooseDecision('reject')"
                    >
                      <strong>Reject</strong>
                    </button>
                  </div>
                  <div v-if="selectedDecision" class="reject-review-box">
                    <label class="decision-label" for="decision-note">
                      {{ selectedDecision === 'approve' ? 'Approval note (optional)' : 'Reason for rejection' }}
                    </label>
                    <textarea
                      id="decision-note"
                      v-model="decisionNote"
                      class="decision-note"
                      rows="6"
                      :placeholder="selectedDecision === 'approve'
                        ? 'Add an optional note for this approval.'
                        : 'Explain why this MOU is being rejected and what needs to be revised.'"
                    />
                  </div>
                  <div class="decision-actions">
                    <CButton color="primary" @click="submitDecision">Submit Admin Decision</CButton>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="footer-actions footer-actions--spread">
            <div class="footer-actions__left">
              <CButton v-if="currentStep > 1" color="secondary" variant="outline" @click="previousStep">
                Previous Step
              </CButton>
            </div>
            <CButton
              v-if="currentStep < 4"
              color="primary"
              @click="nextStep"
            >
              Next Step
            </CButton>
          </div>
        </CCardBody>
      </CCard>
    </div>

    <div v-else class="empty-state empty-state--page">
      <h2>No review document selected</h2>
      <p>{{ isCommittee ? 'Open a review item from the committee dashboard to continue.' : 'Open a document from Review Queue to continue the admin review steps.' }}</p>
      <CButton color="primary" @click="$router.push(isCommittee ? '/dashboard/committee' : '/review-queue')">{{ isCommittee ? 'Open Committee Dashboard' : 'Open Review Queue' }}</CButton>
    </div>
  </div>
</template>

<script>
import api from '@/service/api'
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import AgreementDocumentHeader from '@/views/shared/AgreementDocumentHeader'
import { detectRouteRole } from '@/utils/role-dashboard'
import { readPageCache, writePageCache } from '@/utils/page-cache'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'
import { createWorkflowNotification } from '@/utils/workflow-notifications'
import { showSuccessAlert, showWarningAlert } from '@/utils/sweet-alert'

const COMMITTEE_REVIEW_DETAIL_CACHE_PREFIX = 'committee-review-detail-v2'
const COMMITTEE_REVIEW_DETAIL_CACHE_MAX_AGE = 60 * 1000
const PAGE_TEXT = {
  en: {
    reviewTitle: 'Review',
    reviewSubtitle: 'Open committee-queue agreements, keep change-request rounds inside review, and continue committee work from one shared queue.',
    searchPlaceholder: 'Search by agreement or partner',
    allTypes: 'All Types',
    showing: 'Showing',
    of: 'of',
    reviews: 'reviews',
    columns: { agreement: 'Agreement', type: 'Type', partner: 'Partner', status: 'Status', submittedDate: 'Submitted Date', dueDate: 'Due Date', actions: 'Actions' },
    view: 'View',
    reviewAction: 'Review',
    noReviews: 'No reviews found',
    noReviewsHelp: 'Try changing the filters or wait for more committee queue items.',
    committeeWorkflow: 'Committee Review Workflow',
    committeeProgress: 'Committee progress',
    committeeWorkspace: 'Committee Review Workspace',
    committeeWorkspaceSubtitle: 'Read the agreement draft, add comments, and submit your recommendation to the admin workflow.',
    reviewSummary: 'Review Summary',
    fields: { document: 'Document', submittedOn: 'Submitted on', partner: 'Partner', country: 'Country', currentVersion: 'Current Version', totalVersions: 'Total Versions', dueDate: 'Due date', daysLeft: 'Days left', summary: 'Summary' },
    notSpecified: 'Not specified',
    noSummary: 'No summary provided.',
    previewAndDetail: 'Agreement Preview and Detail',
    loadingDraft: 'Loading Word draft from the database...',
    previewNotReady: 'In-page preview is not ready',
    openDocumentFile: 'Open document file',
    noWordDocument: 'No Word document available',
    noWordDocumentDetail: 'No attached DOC or DOCX was found, and the agreement draft content is also empty.',
    versionHistory: 'Version History',
    noSavedVersions: 'No saved versions yet.',
    readComments: 'Read User And Committee Comments',
    readCommentsHelp: 'Committee and user comments stay here as one running thread until the committee review is completed.',
    changeRequested: 'Change requested',
    changeResolved: 'Change resolved',
    needsChange: 'Needs change',
    selectedTarget: 'Selected target',
    articleName: 'Article Name',
    general: 'General',
    page: 'Page',
    reply: 'Reply',
    resolve: 'Resolve',
    resolved: 'Resolved',
    undoResolve: 'Undo Resolve',
    replyingBelow: 'Replying to this comment below.',
    clear: 'Clear',
    finalReviewTitle: 'Ready to Send',
    finalReviewHelp: 'All comments are resolved. You can now send this agreement to admin.',
    finalReviewBlockedTitle: 'Resolve Comments First',
    finalReviewBlockedNoCommentsTitle: 'Add a Comment First',
    finalReviewBlockedHelp: 'Resolve all open comments to continue.',
    finalReviewBlockedNoComments: 'Add a comment first. The send button will appear after all comments are resolved.',
    totalComments: 'Comments',
    currentStage: 'Status',
    openThreads: 'Open comments',
    currentProgress: 'Progress',
    submitLockedTitle: 'Complete Comment Resolution First',
    submitLockedNoComments: 'Add at least one committee comment and resolve every thread before sending this agreement to admin.',
    submitLockedUnresolved: 'Resolve every committee comment thread before sending this agreement to admin.',
    liveReviewHistory: 'Live Review History',
    liveReviewHistoryHelp: 'Draft versions and discussion activity are visible here before committee sends the agreement to admin final.',
    noLiveReviewHistory: 'No version or comment activity has been recorded yet.',
    draftVersionSaved: 'Draft version saved',
    commentAdded: 'Comment added',
    replyAdded: 'Reply added',
    versionFallbackSummary: 'Draft snapshot saved for review.',
    by: 'by'
  },
  th: {
    reviewTitle: 'ตรวจพิจารณา',
    reviewSubtitle: 'เปิดข้อตกลงในคิวคณะกรรมการ จัดการรอบการขอแก้ไขภายในหน้าตรวจพิจารณา และทำงานต่อจากคิวกลางเดียว',
    searchPlaceholder: 'ค้นหาตามชื่อข้อตกลงหรือคู่สัญญา',
    allTypes: 'ทุกประเภท',
    showing: 'แสดง',
    of: 'จาก',
    reviews: 'รายการ',
    columns: { agreement: 'ข้อตกลง', type: 'ประเภท', partner: 'คู่สัญญา', status: 'สถานะ', submittedDate: 'วันที่ส่ง', dueDate: 'กำหนดส่ง', actions: 'การดำเนินการ' },
    view: 'ดู',
    reviewAction: 'ตรวจพิจารณา',
    noReviews: 'ไม่พบรายการตรวจพิจารณา',
    noReviewsHelp: 'ลองเปลี่ยนตัวกรองหรือรอรายการใหม่จากคิวคณะกรรมการ',
    committeeWorkflow: 'กระบวนการตรวจพิจารณาของคณะกรรมการ',
    committeeProgress: 'ความคืบหน้าของคณะกรรมการ',
    committeeWorkspace: 'พื้นที่ทำงานของคณะกรรมการ',
    committeeWorkspaceSubtitle: 'อ่านร่างข้อตกลง เพิ่มความคิดเห็น และส่งข้อเสนอแนะต่อไปยังขั้นตอนผู้ดูแล',
    reviewSummary: 'สรุปการตรวจพิจารณา',
    fields: { document: 'เอกสาร', submittedOn: 'ส่งเมื่อ', partner: 'คู่สัญญา', country: 'ประเทศ', currentVersion: 'เวอร์ชันปัจจุบัน', totalVersions: 'จำนวนเวอร์ชัน', dueDate: 'กำหนดส่ง', daysLeft: 'วันคงเหลือ', summary: 'สรุป' },
    notSpecified: 'ไม่ได้ระบุ',
    noSummary: 'ไม่มีสรุป',
    previewAndDetail: 'ตัวอย่างและรายละเอียดข้อตกลง',
    loadingDraft: 'กำลังโหลดไฟล์ร่าง Word จากฐานข้อมูล...',
    previewNotReady: 'ยังไม่พร้อมแสดงตัวอย่างในหน้า',
    openDocumentFile: 'เปิดไฟล์เอกสาร',
    noWordDocument: 'ไม่มีเอกสาร Word',
    noWordDocumentDetail: 'ไม่พบไฟล์ DOC หรือ DOCX ที่แนบไว้ และเนื้อหาร่างข้อตกลงก็ว่างเปล่า',
    versionHistory: 'ประวัติเวอร์ชัน',
    noSavedVersions: 'ยังไม่มีเวอร์ชันที่บันทึกไว้',
    readComments: 'อ่านความคิดเห็นของผู้ใช้และคณะกรรมการ',
    readCommentsHelp: 'ความคิดเห็นของคณะกรรมการและผู้ใช้จะถูกรวมเป็นเธรดเดียวจนกว่าการตรวจพิจารณาจะเสร็จสิ้น',
    changeRequested: 'ขอให้แก้ไข',
    changeResolved: 'แก้ไขแล้ว',
    needsChange: 'ต้องแก้ไข',
    selectedTarget: 'ตำแหน่งที่เลือก',
    articleName: 'ชื่อบทความ',
    general: 'ทั่วไป',
    page: 'หน้า',
    reply: 'ตอบกลับ',
    resolve: 'ปิดประเด็น',
    resolved: 'ปิดแล้ว',
    undoResolve: 'ยกเลิกการปิด',
    replyingBelow: 'กำลังตอบกลับความคิดเห็นนี้ด้านล่าง',
    clear: 'ล้าง',
    finalReviewTitle: 'พร้อมส่งให้ผู้ดูแล',
    finalReviewHelp: 'ปิดทุกความคิดเห็นแล้ว คุณสามารถส่งข้อตกลงนี้ให้ผู้ดูแลได้',
    finalReviewBlockedTitle: 'กรุณาปิดความคิดเห็นที่ค้างอยู่ก่อน',
    finalReviewBlockedNoCommentsTitle: 'กรุณาเพิ่มความคิดเห็นก่อน',
    finalReviewBlockedHelp: 'ปิดความคิดเห็นที่ยังเปิดอยู่ทั้งหมดก่อนจึงจะดำเนินการต่อได้',
    finalReviewBlockedNoComments: 'เพิ่มความคิดเห็นก่อน แล้วปุ่มส่งต่อจะปรากฏเมื่อปิดทุกความคิดเห็นเรียบร้อยแล้ว',
    totalComments: 'ความคิดเห็น',
    currentStage: 'สถานะ',
    openThreads: 'ความคิดเห็นที่ยังเปิดอยู่',
    currentProgress: 'ความคืบหน้า',
    submitLockedTitle: 'กรุณาปิดเธรดความคิดเห็นให้ครบก่อน',
    submitLockedNoComments: 'เพิ่มความคิดเห็นของคณะกรรมการอย่างน้อยหนึ่งรายการและปิดทุกเธรดก่อนส่งข้อตกลงนี้ให้ผู้ดูแล',
    submitLockedUnresolved: 'ปิดทุกเธรดความคิดเห็นของคณะกรรมการก่อนส่งข้อตกลงนี้ให้ผู้ดูแล',
    liveReviewHistory: 'ประวัติการตรวจพิจารณาแบบสด',
    liveReviewHistoryHelp: 'ผู้ดูแลสามารถดูเวอร์ชันร่างและกิจกรรมความคิดเห็นก่อนที่คณะกรรมการจะส่งให้พิจารณาขั้นสุดท้าย',
    noLiveReviewHistory: 'ยังไม่มีประวัติเวอร์ชันหรือความคิดเห็น',
    draftVersionSaved: 'บันทึกเวอร์ชันร่าง',
    commentAdded: 'เพิ่มความคิดเห็น',
    replyAdded: 'ตอบกลับความคิดเห็น',
    versionFallbackSummary: 'บันทึกภาพรวมร่างสำหรับการตรวจพิจารณา',
    by: 'โดย'
  }
}

function getProgress(currentStep, status) {
  if (status === 'approved') return 100
  return Math.max(25, Math.min(Number(currentStep || 1), 4) * 25)
}

function addDays(dateLike, count) {
  const date = new Date(dateLike || new Date())
  if (Number.isNaN(date.getTime())) return new Date()
  date.setDate(date.getDate() + count)
  return date
}

function calculateDaysLeft(dateLike) {
  const target = new Date(dateLike)
  if (Number.isNaN(target.getTime())) return 0
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  target.setHours(0, 0, 0, 0)
  return Math.max(0, Math.ceil((target - today) / (1000 * 60 * 60 * 24)))
}

function activityTimestamp(value) {
  if (!value) return 0
  const parsed = new Date(value).getTime()
  if (!Number.isNaN(parsed)) return parsed
  const normalized = String(value || '').trim().replace(' ', 'T')
  const normalizedParsed = new Date(normalized).getTime()
  return Number.isNaN(normalizedParsed) ? 0 : normalizedParsed
}

function normalizeReviewRows(value) {
  if (Array.isArray(value)) return value
  if (value && typeof value === 'object') {
    return Object.values(value).filter(Boolean)
  }
  return []
}

function normalizeComparable(value) {
  return String(value || '').trim().toLowerCase()
}

function pickFirstLangValue(items) {
  return Array.isArray(items) && items.length && items[0] && items[0].value
    ? String(items[0].value).trim()
    : ''
}

function buildProfileDisplayName(profile) {
  if (!(profile && typeof profile === 'object')) return ''
  const userinfo = profile.userinfo || {}
  return [
    pickFirstLangValue(userinfo.prefix),
    pickFirstLangValue(userinfo.firstName),
    pickFirstLangValue(userinfo.lastName)
  ].filter(Boolean).join(' ').trim()
}

function buildCommitteeActorKeys(profile) {
  const displayName = buildProfileDisplayName(profile)
  return [...new Set([
    profile && (profile._id || profile.id),
    profile && profile.email,
    profile && profile.code,
    profile && profile.name,
    profile && profile.displayName,
    displayName
  ].map((value) => String(value || '').trim()).filter(Boolean).map(normalizeComparable))]
}

function committeeStateMatchesProfile(state, profile) {
  if (!(state && profile)) return false
  const actorKeys = buildCommitteeActorKeys(profile)
  if (!actorKeys.length) return false
  const reviewerId = normalizeComparable(state.reviewerId)
  const reviewerName = normalizeComparable(state.reviewerName)
  return actorKeys.some((value) => value === reviewerId || value === reviewerName)
}

function buildCommitteeReviewDetailCacheKey(id, actorKey = '') {
  const agreementKey = String(id || '').trim()
  const reviewerKey = String(actorKey || 'shared')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/gi, '-')
  return `${COMMITTEE_REVIEW_DETAIL_CACHE_PREFIX}-${agreementKey}-${reviewerKey || 'shared'}`
}

function getStageLabel(item) {
  if (!item) return ''
  if (item.status === 'approved') return 'Approved'
  if (item.status === 'revision' || item.status === 'rejected') return 'Returned for User Revision'
  if (isReadyForAdminFinalApproval(item)) return 'Waiting for approval'
  return {
    1: 'Draft Submitted',
    2: 'Committee Notified',
    3: 'Committee Review and Comments'
  }[Math.min(Number(item.currentStep || 1), 3)] || 'Draft Submitted'
}

function getStepMeta() {
  return [
    { id: 1, title: 'Draft Submitted', description: 'User uploads the MOU draft and admin is notified.' },
    { id: 2, title: 'Committee Notified', description: 'The submitted document is automatically routed to all committee members for review.' },
    { id: 3, title: 'Committee Review and Comments', description: 'Committee reviews the draft preview and exchanges article-specific comments with the user.' },
    { id: 4, title: 'Waiting for approval', description: 'After committee approval, admin makes the final approve or disapprove decision.' }
  ]
}

export default {
  name: 'ReviewPage',
  components: { AppSectionHero, AgreementDocumentHeader },
  data() {
    return {
      selectedAgreement: null,
      committeeLandingData: {
        stats: {
          pending: 0,
          inProgress: 0,
          completed: 0
        },
        reviews: []
      },
      committeeLandingTab: 'all',
      committeeLandingFilters: {
        search: '',
        priority: 'all',
        type: 'all'
      },
      draftReviewerIds: [],
      selectedDecision: '',
      decisionNote: '',
      committeeForm: {
        articleKey: 'general',
        decision: 'changes-requested',
        comments: '',
        finalSummary: '',
        page: 1,
        anchor: null,
        section: String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'en').toLowerCase().startsWith('th')
          ? 'ข้อเสนอแนะของคณะกรรมการ'
          : 'Committee recommendation'
      },
      focusedCommitteeSectionKey: '',
      showCommitteeComposer: false,
      committeeCommentSubmitting: false,
      replyTargetThreadId: '',
      replyTargetCommentId: '',
      previewAttachment: null,
      previewLoading: false,
      previewError: '',
      activeCommentId: '',
      committeeWorkspaceView: 'comments',
      steps: getStepMeta()
    }
  },
  computed: {
    localeCode() {
      return String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'en').toLowerCase().startsWith('th') ? 'th' : 'en'
    },
    authProfile() {
      return this.$store && this.$store.getters ? (this.$store.getters['auth/profile'] || null) : null
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    agreementId() {
      return String(this.$route.query.agreementId || this.$route.query.id || '')
    },
    currentRole() {
      return detectRouteRole(this.$store, this.$route)
    },
    committeeDetailCacheKey() {
      if (!this.agreementId) return ''
      const actorIdentity = buildCommitteeActorKeys(this.authProfile)[0] || 'shared'
      return buildCommitteeReviewDetailCacheKey(this.agreementId, actorIdentity)
    },
    isCommittee() {
      return this.currentRole === 'committee'
    },
    committeeLandingStats() {
      return Object.assign({
        pending: 0,
        inProgress: 0,
        completed: 0,
        dueSoon: 0,
        startedToday: 0,
        completedThisWeek: 0,
        averageTime: 0
      }, this.committeeLandingData.stats || {})
    },
    committeeLandingReviews() {
      return Array.isArray(this.committeeLandingData.reviews)
        ? this.committeeLandingData.reviews
        : []
    },
    committeeLandingTypeOptions() {
      return [...new Set(this.committeeLandingReviews.map(item => item.type).filter(Boolean))]
    },
    committeeLandingInReviewCount() {
      return this.committeeLandingReviews.filter(item => this.committeeLandingStatusKey(item) === 'changes-requested').length
    },
    committeeLandingSentToAdminCount() {
      return this.committeeLandingReviews.filter(item => this.committeeLandingStatusKey(item) === 'sent-to-admin').length
    },
    committeeLandingHeroStats() {
      return [
        {
          label: this.localeCode === 'th' ? 'รายการทั้งหมด' : 'Total Reviews',
          value: this.committeeLandingReviews.length,
          icon: 'cil-list-rich',
          iconClass: 'app-section-stat__icon--total',
          clickable: true,
          active: this.committeeLandingTab === 'all'
        },
        {
          label: this.localeCode === 'th' ? 'ตรวจพิจารณา' : 'Review',
          value: this.committeeLandingInReviewCount,
          icon: 'cil-hourglass',
          iconClass: 'app-section-stat__icon--attention',
          clickable: true,
          active: this.committeeLandingTab === 'changes-requested'
        },
        {
          label: this.localeCode === 'th' ? 'ส่งให้ผู้ดูแลแล้ว' : 'Sent to Admin',
          value: this.committeeLandingSentToAdminCount,
          icon: 'cil-share-boxed',
          iconClass: 'app-section-stat__icon--active',
          clickable: true,
          active: this.committeeLandingTab === 'sent-to-admin'
        }
      ]
    },
    committeeLandingFilteredReviews() {
      let items = [...this.committeeLandingReviews]
      if (this.committeeLandingTab === 'changes-requested') {
        items = items.filter(item => this.committeeLandingStatusKey(item) === 'changes-requested')
      } else if (this.committeeLandingTab === 'sent-to-admin') {
        items = items.filter(item => this.committeeLandingStatusKey(item) === 'sent-to-admin')
      }
      if (this.committeeLandingFilters.type !== 'all') {
        items = items.filter(item => item.type === this.committeeLandingFilters.type)
      }
      if (this.committeeLandingFilters.search) {
        const query = this.committeeLandingFilters.search.toLowerCase()
        items = items.filter(item =>
          String(item.agreement || '').toLowerCase().includes(query) ||
          String(item.partner || '').toLowerCase().includes(query)
        )
      }
      return items
    },
    currentStep() {
      return this.selectedAgreement ? this.selectedAgreement.currentStep : 1
    },
    activeStep() {
      return this.steps.find(step => step.id === this.currentStep) || this.steps[0]
    },
    progress() {
      if (!this.selectedAgreement) return 0
      return getProgress(this.selectedAgreement.currentStep, this.selectedAgreement.status)
    },
    stageLabel() {
      if (!this.selectedAgreement) return ''
      if (!this.isCommittee) return getStageLabel(this.selectedAgreement)
      if (this.selectedAgreement.reviewStatus === 'revision') return this.localeCode === 'th' ? 'ตรวจพิจารณา' : 'Review'
      if (this.selectedAgreement.reviewStatus === 'approved') return 'Approved'
      if (this.selectedAgreement.reviewStatus === 'rejected') return 'Rejected'
      if (isReadyForAdminFinalApproval(this.selectedAgreement)) return 'Sent to Admin'
      return this.localeCode === 'th' ? 'ตรวจพิจารณา' : 'Review'
    },
    statusLabel() {
      if (!this.selectedAgreement) return ''
      if (this.isCommittee) {
        return {
          pending: 'Open',
          'in-progress': this.localeCode === 'th' ? 'ตรวจพิจารณา' : 'Review',
          'sent-to-admin': 'Sent to Admin',
          approved: 'Approved',
          rejected: 'Rejected',
          completed: 'Submitted'
        }[this.selectedAgreement.status] || this.selectedAgreement.status
      }
      return {
        draft: 'Drafted',
        pending: this.localeCode === 'th' ? 'ตรวจพิจารณา' : 'Review',
        approved: 'Approved',
        rejected: 'Rejected'
      }[this.selectedAgreement.status] || this.selectedAgreement.status
    },
    committeeLabel() {
      if (!this.selectedAgreement || !this.selectedAgreement.committees.length) return 'Committee queue active'
      return this.selectedAgreement.committees.join(', ')
    },
    committeeMembers() {
      return this.selectedAgreement && Array.isArray(this.selectedAgreement.committees)
        ? this.selectedAgreement.committees
        : []
    },
    committeeCount() {
      return this.committeeMembers.length
    },
    commentCount() {
      return this.selectedAgreement && Array.isArray(this.selectedAgreement.comments)
        ? this.selectedAgreement.comments.length
        : 0
    },
    adminReviewWorkspacePath() {
      const agreementId = this.selectedAgreement
        ? String(this.selectedAgreement.agreementId || this.selectedAgreement.id || '')
        : String(this.agreementId || '')
      if (!agreementId) return '/review-queue'
      return isReadyForAdminFinalApproval(this.selectedAgreement)
        ? `/review-queue/${agreementId}/final-approval`
        : `/review-queue/${agreementId}/review`
    },
    selectedReviewers() {
      if (!this.selectedAgreement || !this.selectedAgreement.availableReviewers) return []
      if (this.draftReviewerIds.length) {
        return this.selectedAgreement.availableReviewers.filter(reviewer => this.draftReviewerIds.includes(reviewer.id))
      }
      return Number(this.selectedAgreement.currentStep || 1) >= 2
        ? this.selectedAgreement.availableReviewers
        : []
    },
    selectedReviewerNames() {
      return this.selectedReviewers.map(reviewer => reviewer.name)
    },
    previewUrl() {
      if (!this.previewAttachment) return ''
      return this.previewAttachment.url || this.previewAttachment.fileUrl || ''
    },
    previewAttachmentLabel() {
      if (!this.previewAttachment) return this.selectedAgreement ? this.selectedAgreement.fileLabel : 'MOU Draft'
      return this.previewAttachment.originalName || this.previewAttachment.fileName || this.selectedAgreement.fileLabel
    },
    draftPreviewSections() {
      return this.selectedAgreement && Array.isArray(this.selectedAgreement.draftPreviewSections)
        ? this.selectedAgreement.draftPreviewSections
        : []
    },
    baseDraftPreviewSections() {
      let sections = []

      if (this.draftPreviewSections.length) {
        sections = this.draftPreviewSections
      } else {
        const latestVersion = this.versions.length ? this.versions[0] : null
        const versionDrafts = Array.isArray(latestVersion && latestVersion.articleDrafts) ? latestVersion.articleDrafts : []
        if (versionDrafts.length) {
          sections = versionDrafts
            .map((item, index) => ({
              key: String(item && item.key ? item.key : `article-${index + 1}`),
              title: String(item && item.title ? item.title : `Article ${index + 1}`),
              content: String(item && item.content ? item.content : '')
            }))
            .filter((item) => item.content)
        } else {
          const versionDocument = latestVersion && latestVersion.customDocument ? latestVersion.customDocument : null
          if (versionDocument && versionDocument.content) {
            sections = [{
              key: 'version-custom-document',
              title: versionDocument.title || 'Draft Document',
              content: versionDocument.content
            }]
          } else if (this.selectedAgreement && this.selectedAgreement.summary) {
            sections = [{
              key: 'summary',
              title: 'Summary',
              content: this.selectedAgreement.summary
            }]
          }
        }
      }

      const normalizedSections = Array.isArray(sections)
        ? sections.filter((section) => section && section.title)
        : []
      const detailLines = []

      if (this.selectedAgreement) {
        if (this.selectedAgreement.title) detailLines.push(this.selectedAgreement.title)
        if (this.selectedAgreement.type) detailLines.push(`Type: ${this.selectedAgreement.type}`)
        if (this.selectedAgreement.partner) detailLines.push(`Partner: ${this.selectedAgreement.partner}`)
        if (this.selectedAgreement.country) detailLines.push(`Country: ${this.selectedAgreement.country}`)
      }

      const metadataSection = detailLines.length
        ? {
            key: 'agreement-title',
            title: 'Agreement Title',
            content: detailLines.join('\n')
          }
        : null

      return metadataSection
        ? [metadataSection, ...normalizedSections.filter(section => section.key !== metadataSection.key)]
        : normalizedSections
    },
    displayDraftPreviewSections() {
      const sections = this.baseDraftPreviewSections

      if (!sections.length) return []

      const focusKey = String(this.focusedCommitteeSectionKey || '').trim()
      if (!focusKey) return sections

      const focusIndex = sections.findIndex(section => String(section.key) === focusKey)
      if (focusIndex <= 0) return sections

      const focusedSection = sections[focusIndex]
      return [focusedSection, ...sections.slice(0, focusIndex), ...sections.slice(focusIndex + 1)]
    },

    activeComment() {
      if (!this.selectedAgreement) return null
      const comments = Array.isArray(this.selectedAgreement.comments) ? this.selectedAgreement.comments : []
      return comments.find(comment => comment.id === this.activeCommentId) || null
    },
    articleOptions() {
      const sectionOptions = this.baseDraftPreviewSections
        .map((section, index) => ({
          key: String(section && section.key ? section.key : `article-${index + 1}`),
          title: String(section && section.title ? section.title : `Article ${index + 1}`)
        }))
        .filter((item) => item.title)

      if (sectionOptions.length) return sectionOptions

      return [{ key: 'general', title: 'General' }]
    },
    commentThreads() {
      const comments = this.selectedAgreement && Array.isArray(this.selectedAgreement.comments) ? this.selectedAgreement.comments : []
      const map = new Map()
      const seenComments = new Set()
      comments.forEach((item) => {
        const fingerprint = [
          item.parentId || '',
          item.threadId || '',
          item.author || '',
          item.role || '',
          item.articleKey || '',
          item.articleTitle || item.section || '',
          item.page || '',
          item.versionNumber || '',
          item.content || item.copy || ''
        ].join('::')
        if (seenComments.has(fingerprint)) return
        seenComments.add(fingerprint)
        const threadId = item.threadId || item.commentId || item.id
        if (!map.has(threadId)) {
          map.set(threadId, { threadId, root: null, replies: [], status: item.status === 'resolved' ? 'resolved' : 'open' })
        }
        const bucket = map.get(threadId)
        if (item.parentId) {
          bucket.replies.push(item)
        } else if (!bucket.root) {
          bucket.root = item
        }
        if (item.status !== 'resolved') {
          bucket.status = 'open'
        }
      })
      return Array.from(map.values()).filter(item => item.root)
    },
    adminLiveReviewTimeline() {
      if (!(this.selectedAgreement && !this.isCommittee)) return []
      const versionItems = this.versions.map((version, index) => {
        const versionNumber = version && version.versionNumber ? version.versionNumber : ''
        const actor = String(version && version.createdBy ? version.createdBy : '').trim()
        const role = String(version && version.createdByRole ? version.createdByRole : '').trim()
        const source = String(version && version.source ? version.source : '').trim()
        const metaParts = [
          role || source || 'workflow',
          actor ? `${this.text.by} ${actor}` : '',
          versionNumber ? `v${versionNumber}` : ''
        ].filter(Boolean)
        return {
          id: `version-${versionNumber || index}`,
          type: 'version',
          timestamp: version && version.createdAt ? version.createdAt : '',
          title: versionNumber ? `${this.text.draftVersionSaved} v${versionNumber}` : this.text.draftVersionSaved,
          meta: metaParts.join(' · '),
          summary: String(version && version.summary ? version.summary : this.text.versionFallbackSummary)
        }
      })
      const comments = Array.isArray(this.selectedAgreement.comments) ? this.selectedAgreement.comments : []
      const commentItems = comments.map((comment, index) => {
        const isReply = !!(comment && comment.parentId)
        const roleLabel = comment && (comment.roleLabel || comment.role) ? (comment.roleLabel || comment.role) : this.text.general
        const versionNumber = comment && comment.versionNumber ? comment.versionNumber : ''
        const section = comment && (comment.articleTitle || comment.section) ? (comment.articleTitle || comment.section) : ''
        const metaParts = [
          roleLabel,
          comment && comment.author ? `${this.text.by} ${comment.author}` : '',
          section,
          versionNumber ? `v${versionNumber}` : ''
        ].filter(Boolean)
        return {
          id: `comment-${comment && (comment.commentId || comment.id) ? (comment.commentId || comment.id) : index}`,
          type: 'comment',
          timestamp: comment && (comment.time || comment.date) ? (comment.time || comment.date) : '',
          title: isReply ? this.text.replyAdded : this.text.commentAdded,
          meta: metaParts.join(' · '),
          summary: String(comment && (comment.copy || comment.content) ? (comment.copy || comment.content) : '')
        }
      })
      return [...versionItems, ...commentItems]
        .filter(item => item.timestamp || item.summary)
        .sort((left, right) => activityTimestamp(right.timestamp) - activityTimestamp(left.timestamp))
        .slice(0, 12)
    },
    unresolvedCommentThreadCount() {
      return this.commentThreads.filter(thread => thread.status !== 'resolved').length
    },
    currentCommitteeMemberState() {
      const states = this.selectedAgreement && Array.isArray(this.selectedAgreement.committeeMemberStates)
        ? this.selectedAgreement.committeeMemberStates
        : []
      return states.find((item) => committeeStateMatchesProfile(item, this.authProfile)) || null
    },
    currentCommitteeMemberApproved() {
      return normalizeComparable(this.currentCommitteeMemberState && this.currentCommitteeMemberState.decision) === 'approve'
    },
    committeeFinalAlreadySent() {
      return !!(this.selectedAgreement
        && this.selectedAgreement.readyForAdminFinalApproval
        && normalizeComparable(this.selectedAgreement.committeeDecision) === 'approve'
        && Number(this.selectedAgreement.currentStep || 1) >= 4)
    },
    showCommitteeFinalPanel() {
      return !!(this.isCommittee && this.selectedAgreement)
    },
    canSubmitCommitteeFinal() {
      if (!(this.isCommittee && this.selectedAgreement)) return false
      if (this.committeeFinalAlreadySent) return false
      if (this.currentCommitteeMemberApproved) return false
      return this.commentThreads.length > 0 && this.unresolvedCommentThreadCount === 0
    },
    committeeSubmitBlockedTitle() {
      if (this.committeeFinalAlreadySent) {
        return this.localeCode === 'th' ? 'คณะกรรมการส่งให้ผู้ดูแลแล้ว' : 'Already Sent To Admin'
      }
      if (this.currentCommitteeMemberApproved) {
        return this.localeCode === 'th' ? 'บันทึกการอนุมัติของคุณแล้ว' : 'Your Approval Was Recorded'
      }
      if (this.commentThreads.length === 0) return this.text.finalReviewBlockedNoCommentsTitle
      return this.text.finalReviewBlockedTitle
    },
    committeeSubmitBlockedHelp() {
      if (this.committeeFinalAlreadySent) {
        return this.localeCode === 'th'
          ? 'ข้อตกลงนี้พร้อมสำหรับการตัดสินใจขั้นสุดท้ายของผู้ดูแลแล้ว'
          : 'This agreement is already waiting for the final admin decision.'
      }
      if (this.currentCommitteeMemberApproved) {
        const pendingNames = this.selectedAgreement
          && this.selectedAgreement.committeeApprovalProgress
          && Array.isArray(this.selectedAgreement.committeeApprovalProgress.pendingReviewers)
          ? this.selectedAgreement.committeeApprovalProgress.pendingReviewers.filter(Boolean)
          : []
        if (pendingNames.length) {
          return this.localeCode === 'th'
            ? `กำลังรอการอนุมัติจาก: ${pendingNames.join(', ')}`
            : `Waiting for approval from: ${pendingNames.join(', ')}`
        }
        return this.localeCode === 'th'
          ? 'ระบบบันทึกการอนุมัติของคุณแล้ว'
          : 'Your committee approval has already been saved.'
      }
      if (this.commentThreads.length === 0) return this.text.finalReviewBlockedNoComments
      return this.text.finalReviewBlockedHelp
    },
    versions() {
      return this.selectedAgreement && Array.isArray(this.selectedAgreement.versions)
        ? [...this.selectedAgreement.versions].reverse()
        : []
    },
    latestVersionNumber() {
      return this.versions.length ? this.versions[0].versionNumber : 1
    },
    committeeSubmitLabel() {
      return this.localeCode === 'th'
        ? 'ส่งให้ผู้ดูแลพิจารณาขั้นสุดท้าย'
        : 'Submit To Admin Final'
    },
    committeeSubmitActionLabel() {
      if (this.committeeFinalAlreadySent) {
        return this.localeCode === 'th' ? 'ส่งให้ผู้ดูแลแล้ว' : 'Sent To Admin'
      }
      if (this.currentCommitteeMemberApproved) {
        return this.localeCode === 'th' ? 'บันทึกการอนุมัติแล้ว' : 'Approval Recorded'
      }
      return this.committeeSubmitLabel
    }
  },
  watch: {
    agreementId: {
      immediate: true,
      handler() {
        this.loadAgreement()
      }
    },
    currentRole() {
      this.loadAgreement()
    }
  },
  mounted() {
  },
  beforeDestroy() {
  },
  methods: {
    mapAgreementToCommitteeLanding(item) {
      if (!item) return null
      const submittedAt = item.submittedAt || item.assignedDate || item.startDate || item.createdAt || new Date().toISOString()
      const normalizedStatus = String(item.status || '').trim().toLowerCase()
      const status = normalizedStatus === 'rejected'
        ? 'rejected'
        : normalizedStatus === 'approved'
          ? 'approved'
        : normalizedStatus === 'sent-to-admin'
          ? 'sent-to-admin'
        : normalizedStatus === 'completed'
          ? 'approved'
        : normalizedStatus === 'in-progress' || normalizedStatus === 'changes-requested' || normalizedStatus === 'revision'
          ? 'changes-requested'
        : 'pending'
      const dueDate = item.dueDate || addDays(submittedAt, status === 'pending' ? 7 : 10)

      return {
        id: item.documentCode || item.id,
        agreementId: item.agreementId || item.id,
        agreement: item.agreement || item.title || 'Untitled agreement',
        partner: item.partner || 'Partner institution',
        type: item.type || 'Agreement',
        status,
        submittedAt,
        dueDate,
        daysLeft: calculateDaysLeft(dueDate)
      }
    },
    async loadAgreement(options = {}) {
      if (!this.agreementId) {
        this.selectedAgreement = null
        if (this.isCommittee) {
          await this.loadCommitteeLanding()
        }
        return
      }
      const previousAgreementId = this.selectedAgreement
        ? String(this.selectedAgreement.agreementId || this.selectedAgreement.id || '')
        : ''
      const cached = this.isCommittee && !options.forceRefresh
        ? readPageCache(this.committeeDetailCacheKey, COMMITTEE_REVIEW_DETAIL_CACHE_MAX_AGE)
        : null

      if (this.isCommittee && cached && typeof cached === 'object' && cached.item) {
        this.selectedAgreement = cached.item
        if (!options.silent) {
          this.loadPreviewAttachment()
        }
      }

      const response = this.isCommittee
        ? await api.committeeReview('detail', { id: this.agreementId })
        : await api.adminReview('detail', { id: this.agreementId })
      let item = response && response.data && response.data.data ? response.data.data : null
      if (!this.isCommittee) {
        item = await this.ensureCommitteeRouting(item)
        if (this.shouldRedirectToAdminReviewWorkspace(item)) {
          this.$router.replace(this.buildAdminReviewWorkspacePath(item))
          return
        }
      }
      const nextAgreementId = item ? String(item.agreementId || item.id || '') : ''
      this.selectedAgreement = item
      this.draftReviewerIds = item && item.assignedReviewerIds ? [...item.assignedReviewerIds] : []
      this.selectedDecision = ''
      this.decisionNote = item ? item.decisionNote || '' : ''
      this.activeCommentId = item && item.comments && item.comments.length ? item.comments[0].id : ''
      if (previousAgreementId !== nextAgreementId) {
        this.committeeWorkspaceView = isReadyForAdminFinalApproval(item) ? 'submit' : 'comments'
      }
      if (this.isCommittee) {
        const firstArticle = item && Array.isArray(item.articleOptions) && item.articleOptions.length ? item.articleOptions[0] : null
        const hasCurrentArticle = firstArticle && Array.isArray(item.articleOptions)
          ? item.articleOptions.some(option => option.key === this.committeeForm.articleKey)
          : false
        if (firstArticle && !hasCurrentArticle) {
          this.committeeForm.articleKey = firstArticle.key
        }
        this.persistCommitteeDetailCache(item)
      }
      if (!options.silent) {
        this.loadPreviewAttachment()
      }
    },
    async ensureCommitteeRouting(item) {
      if (!item || item.status === 'approved') return item
      if (Number(item.currentStep || 1) > 2) return item
      if (!Array.isArray(item.availableReviewers) || !item.availableReviewers.length) return item
      const assignedReviewerIds = item.availableReviewers.map(reviewer => reviewer.id)
      try {
        const response = await api.adminReview('update', {
          id: item.id,
          assignedReviewerIds,
          currentStep: 3,
          status: 'pending'
        })
        return response && response.data && response.data.data ? response.data.data : {
          ...item,
          assignedReviewerIds,
          committees: item.availableReviewers.map(reviewer => reviewer.name),
          currentStep: 3,
          status: 'pending'
        }
      } catch (error) {
        return {
          ...item,
          assignedReviewerIds,
          committees: item.availableReviewers.map(reviewer => reviewer.name),
          currentStep: 3,
          status: 'pending'
        }
      }
    },
    shouldRedirectToAdminReviewWorkspace(item) {
      return !this.isCommittee && !!item && item.status !== 'approved'
    },
    buildAdminReviewWorkspacePath(item) {
      const agreementId = item ? String(item.agreementId || item.id || '') : ''
      if (!agreementId) return '/review-queue'
      return isReadyForAdminFinalApproval(item)
        ? `/review-queue/${agreementId}/final-approval`
        : `/review-queue/${agreementId}/review`
    },
    async loadCommitteeLanding() {
      try {
        const response = await api.committeeReview('dashboard')
        const data = response && response.data && response.data.data ? response.data.data : {}
        let reviews = normalizeReviewRows(data.reviews)
          .map(this.mapAgreementToCommitteeLanding)
          .filter(Boolean)

        if (!reviews.length) {
          try {
            const agreementsResponse = await api.adminReview('agreements')
            const agreementRows = agreementsResponse && agreementsResponse.data && Array.isArray(agreementsResponse.data.data)
              ? agreementsResponse.data.data
              : []
            reviews = agreementRows
              .map(this.mapAgreementToCommitteeLanding)
              .filter(item => item && ['changes-requested', 'pending', 'sent-to-admin'].includes(item.status))
          } catch (fallbackError) {
            reviews = []
          }
        }

        this.committeeLandingData = {
          stats: reviews.length
            ? {
                pending: reviews.filter(item => item.status === 'pending').length,
                inProgress: reviews.filter(item => item.status === 'changes-requested').length,
                completed: reviews.filter(item => item.status === 'sent-to-admin').length
              }
            : Object.assign({ pending: 0, inProgress: 0, completed: 0 }, data.stats || {}),
          reviews
        }
        this.committeeLandingTab = this.getPreferredCommitteeLandingTab()
      } catch (error) {
        this.committeeLandingData = {
          stats: { pending: 0, inProgress: 0, completed: 0 },
          reviews: []
        }
        this.committeeLandingTab = 'all'
      }
    },
    openCommitteeReview(review) {
      if (!review) return
      this.$router.push({ path: '/review', query: { id: review.agreementId || review.id } })
    },
    clearCommitteeLandingFilters() {
      this.committeeLandingFilters = {
        search: '',
        priority: 'all',
        type: 'all'
      }
      this.committeeLandingTab = this.getPreferredCommitteeLandingTab()
    },
    persistCommitteeDetailCache(item = this.selectedAgreement) {
      if (!(this.isCommittee && this.agreementId && item && this.committeeDetailCacheKey)) return
      writePageCache(this.committeeDetailCacheKey, {
        item,
        lastUpdatedAt: new Date().toISOString()
      })
    },
    getPreferredCommitteeLandingTab() {
      return 'all'
    },
    selectCommitteeLandingStatus(stat) {
      const label = String(stat && stat.label ? stat.label : '').toLowerCase()
      const nextFilter = (label === 'in review' || label === 'review' || label === 'ตรวจพิจารณา')
        ? 'changes-requested'
        : label === 'sent to admin'
          ? 'sent-to-admin'
          : 'all'
      this.committeeLandingTab = this.committeeLandingTab === nextFilter ? 'all' : nextFilter
    },
    committeeLandingStatusKey(review) {
      if (!(review && review.status)) return 'changes-requested'
      if (review.status === 'approved') return 'approved'
      if (review.status === 'rejected') return 'rejected'
      if (review.status === 'sent-to-admin') return 'sent-to-admin'
      return 'changes-requested'
    },
    committeeLandingStatusText(review) {
      const statusKey = this.committeeLandingStatusKey(review)
      if (statusKey === 'rejected') {
        return this.localeCode === 'th' ? 'ถูกปฏิเสธ' : 'Rejected'
      }
      if (statusKey === 'sent-to-admin') {
        return this.localeCode === 'th' ? 'ส่งให้ผู้ดูแลแล้ว' : 'Sent to Admin'
      }
      return statusKey === 'approved'
        ? (this.localeCode === 'th' ? 'อนุมัติแล้ว' : 'Approved')
        : (this.localeCode === 'th' ? 'ตรวจพิจารณา' : 'Review')
    },
    committeeLandingStageText(review) {
      if (!(review && review.status)) return this.localeCode === 'th' ? 'ตรวจพิจารณา' : 'Review'
      if (review.status === 'approved') return this.localeCode === 'th' ? 'ผู้ดูแลอนุมัติข้อตกลงนี้แล้ว' : 'This agreement was approved by admin'
      if (review.status === 'rejected') return this.localeCode === 'th' ? 'ผู้ดูแลปฏิเสธข้อตกลงนี้แล้ว' : 'This agreement was rejected by admin'
      if (review.status === 'pending') return this.localeCode === 'th' ? 'กำลังรอในคิวคณะกรรมการ' : 'Waiting in the committee queue'
      if (review.status === 'sent-to-admin') return this.localeCode === 'th' ? 'คณะกรรมการทุกคนส่งต่อให้ผู้ดูแลเพื่อพิจารณาขั้นสุดท้ายแล้ว' : 'All committee members have sent this agreement to admin for the final decision'
      if (review.status === 'changes-requested' || review.status === 'in-progress') return this.localeCode === 'th' ? 'มีการขอแก้ไขและยังอยู่ระหว่างการพิจารณา' : 'Change requested and still in review'
      return this.localeCode === 'th' ? 'คณะกรรมการตรวจพิจารณาเสร็จแล้ว' : 'Committee review completed'
    },
    committeeLandingActionLabel(review) {
      return this.text.reviewAction
    },
    assignedCommitteeAudienceKeys(item) {
      if (!(item && Array.isArray(item.availableReviewers) && Array.isArray(item.assignedReviewerIds))) return []
      return item.availableReviewers
        .filter(reviewer => item.assignedReviewerIds.includes(reviewer.id))
        .flatMap((reviewer) => ([
          reviewer && reviewer.email,
          reviewer && reviewer.id,
          reviewer && reviewer.department,
          reviewer && reviewer.role,
          reviewer && reviewer.name
        ]))
        .map(value => String(value || '').trim().toLowerCase())
        .filter(Boolean)
        .filter((value, index, rows) => rows.indexOf(value) === index)
    },
    truncateText(value, limit = 120) {
      const text = String(value || '')
      return text.length <= limit ? text : `${text.slice(0, limit)}...`
    },
    normalizeReviewTargetKey(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },
    isCommitteeComment(comment) {
      const role = String(comment && (comment.role || comment.roleLabel) || '').toLowerCase()
      return role.includes('committee')
    },
    isCommitteeThread(thread) {
      return !!(thread && thread.root && this.isCommitteeComment(thread.root))
    },
    commentTargetTitle(comment) {
      return String(comment && (comment.articleTitle || comment.section) ? (comment.articleTitle || comment.section) : this.text.general)
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    commentAnchorText(comment) {
      const anchor = comment && comment.anchor && typeof comment.anchor === 'object' ? comment.anchor : null
      return anchor && anchor.text ? String(anchor.text).trim() : ''
    },
    clearCommitteeAnchor() {
      this.committeeForm.anchor = null
    },
    captureCommitteeTextAnchor(section, event) {
      if (!(section && event && event.currentTarget) || typeof window === 'undefined') return
      const selection = window.getSelection ? window.getSelection() : null
      if (!(selection && selection.rangeCount)) return
      const selectedText = String(selection.toString() || '').trim()
      if (!selectedText) return
      const target = event.currentTarget
      const range = selection.getRangeAt(0)
      if (!target.contains(range.commonAncestorContainer)) return

      const preRange = range.cloneRange()
      preRange.selectNodeContents(target)
      preRange.setEnd(range.startContainer, range.startOffset)
      const rawSelection = String(range.toString() || '')
      const leadingTrim = rawSelection.length - rawSelection.replace(/^\s+/, '').length
      const trailingTrim = rawSelection.length - rawSelection.replace(/\s+$/, '').length
      const startOffset = preRange.toString().length + leadingTrim
      const endOffset = preRange.toString().length + rawSelection.length - trailingTrim
      const content = String(section.content || '')
      const safeStart = Math.max(0, Math.min(startOffset, content.length))
      const safeEnd = Math.max(safeStart, Math.min(endOffset, content.length))
      const text = content.slice(safeStart, safeEnd).trim() || selectedText
      if (!text) return

      this.committeeForm.articleKey = section.key || 'general'
      this.committeeForm.section = section.title || this.text.general
      this.committeeForm.anchor = {
        type: 'text',
        text,
        startOffset: safeStart,
        endOffset: safeEnd,
        beforeText: content.slice(Math.max(0, safeStart - 80), safeStart),
        afterText: content.slice(safeEnd, safeEnd + 80),
        sectionKey: section.key || '',
        sectionTitle: section.title || ''
      }
    },
    buildSectionAnchorRanges(section) {
      const content = String(section && section.content ? section.content : '')
      if (!content) return []
      const ranges = []
      this.sectionChangeThreads(section).forEach((thread) => {
        const anchor = thread && thread.root && thread.root.anchor ? thread.root.anchor : null
        const text = anchor && anchor.text ? String(anchor.text).trim() : ''
        if (!text) return
        let start = Number(anchor.startOffset)
        let end = Number(anchor.endOffset)
        const offsetMatches = Number.isFinite(start) && Number.isFinite(end) && start >= 0 && end > start && content.slice(start, end).trim() === text
        if (!offsetMatches) {
          start = content.indexOf(text)
          end = start >= 0 ? start + text.length : -1
        }
        if (start < 0 || end <= start) return
        ranges.push({
          start,
          end,
          resolved: thread.status === 'resolved',
          label: thread.root.copy || thread.root.content || ''
        })
      })
      return ranges
        .sort((left, right) => left.start - right.start || right.end - left.end)
        .reduce((items, range) => {
          const previous = items[items.length - 1]
          if (previous && range.start < previous.end) return items
          items.push(range)
          return items
        }, [])
    },
    renderHighlightedSectionContent(section) {
      const content = String(section && section.content ? section.content : '')
      const ranges = this.buildSectionAnchorRanges(section)
      if (!ranges.length) return this.escapeHtml(content)
      let cursor = 0
      let html = ''
      ranges.forEach((range) => {
        html += this.escapeHtml(content.slice(cursor, range.start))
        const classes = ['comment-anchor-highlight']
        if (range.resolved) classes.push('comment-anchor-highlight--resolved')
        html += `<mark class="${classes.join(' ')}" title="${this.escapeHtml(range.label)}">${this.escapeHtml(content.slice(range.start, range.end))}</mark>`
        cursor = range.end
      })
      html += this.escapeHtml(content.slice(cursor))
      return html
    },
    commentTargetKey(comment) {
      if (!(comment && typeof comment === 'object')) return ''
      return this.normalizeReviewTargetKey(comment.articleKey || comment.articleTitle || comment.section || '')
    },
    sectionTargetKey(section) {
      if (!(section && typeof section === 'object')) return ''
      return this.normalizeReviewTargetKey(section.key || section.title || '')
    },
    threadMatchesSection(thread, section) {
      if (!(thread && thread.root && section)) return false
      const sectionKeys = [
        section.key,
        section.title
      ].map(value => this.normalizeReviewTargetKey(value)).filter(Boolean)
      const commentKeys = [
        thread.root.articleKey,
        thread.root.articleTitle,
        thread.root.section
      ].map(value => this.normalizeReviewTargetKey(value)).filter(Boolean)
      return commentKeys.some(key => sectionKeys.includes(key))
    },
    sectionChangeThreads(section) {
      return this.commentThreads.filter(thread => this.isCommitteeThread(thread) && this.threadMatchesSection(thread, section))
    },
    sectionOpenChangeCount(section) {
      return this.sectionChangeThreads(section).filter(thread => thread.status !== 'resolved').length
    },
    sectionChangeLabel(section) {
      const openCount = this.sectionOpenChangeCount(section)
      if (openCount > 0) return `${openCount} ${this.text.needsChange}`
      return this.text.changeResolved
    },
    draftPreviewCardClass(section) {
      const sectionKey = String(section && section.key ? section.key : '')
      const changeThreads = this.sectionChangeThreads(section)
      const openCount = changeThreads.filter(thread => thread.status !== 'resolved').length
      return {
        'draft-preview-card--focus': this.focusedCommitteeSectionKey === sectionKey,
        'draft-preview-card--has-change': changeThreads.length > 0,
        'draft-preview-card--open-change': openCount > 0
      }
    },
    committeeSectionId(key) {
      return `committee-section-${String(key || 'section').replace(/[^a-zA-Z0-9_-]/g, '-')}`
    },
    focusCommitteeSection(articleKey) {
      if (!articleKey) return
      this.focusedCommitteeSectionKey = articleKey
      this.$nextTick(() => {
        const target = document.getElementById(this.committeeSectionId(articleKey))
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    openCommitteeThreadContext(thread) {
      if (!(thread && thread.root)) return
      this.activeCommentId = thread.root.id || thread.root.commentId || ''
      this.focusCommitteeSection(thread.root.articleKey || '')
    },
    openCommitteeComposer() {
      this.showCommitteeComposer = true
      this.clearCommitteeReplyTarget()
      const firstArticle = this.articleOptions.length ? this.articleOptions[0] : null
      if (firstArticle && !(this.committeeForm.anchor && this.committeeForm.anchor.sectionKey)) {
        this.committeeForm.articleKey = firstArticle.key
        this.committeeForm.section = firstArticle.title
      }
      this.$nextTick(() => {
        const target = document.getElementById('committee-note')
        if (target) target.focus()
      })
    },
    closeCommitteeComposer() {
      this.showCommitteeComposer = false
      this.clearCommitteeReplyTarget()
      this.committeeForm.comments = ''
      this.clearCommitteeAnchor()
    },
    applyCommitteeArticleSelection() {
      const selected = this.articleOptions.find(item => item.key === this.committeeForm.articleKey)
      if (selected && (!this.committeeForm.section || this.committeeForm.section === 'Committee recommendation')) {
        this.committeeForm.section = selected.title
      }
      if (selected) {
        if (this.committeeForm.anchor && this.committeeForm.anchor.sectionKey !== selected.key) this.clearCommitteeAnchor()
        this.focusCommitteeSection(selected.key)
      }
    },
    async loadPreviewAttachment() {
      if (!this.selectedAgreement) return
      this.previewLoading = true
      this.previewError = ''
      this.previewAttachment = null

      try {
        const response = await api.attachments('list', {
          ownerType: this.selectedAgreement.attachmentOwnerType || 'mou-review',
          ownerId: this.selectedAgreement.attachmentOwnerId || `mou-${this.selectedAgreement.id}`
        })
        const rawList = Array.isArray(response && response.data && response.data.data)
          ? response.data.data
          : Array.isArray(response && response.data)
            ? response.data
            : []

        const documentAttachment = rawList.find(item => {
          const mime = String(item.mimeType || '').toLowerCase()
          const name = String(item.originalName || item.fileName || '').toLowerCase()
          return mime.includes('msword') || mime.includes('wordprocessingml') || name.endsWith('.doc') || name.endsWith('.docx')
        }) || null

        this.previewAttachment = documentAttachment || this.buildAgreementDraftAttachment()
        if (!this.previewAttachment) {
          this.previewError = this.localeCode === 'th' ? 'ยังไม่มีไฟล์ร่าง Word สำหรับ MOU นี้' : 'No Word draft is attached for this MOU yet.'
        }
      } catch (error) {
        this.previewAttachment = this.buildAgreementDraftAttachment()
        if (!this.previewAttachment && !this.displayDraftPreviewSections.length) {
          this.previewError = this.localeCode === 'th' ? 'ไม่สามารถโหลดเอกสาร Word ของ MOU จากฐานข้อมูลได้' : 'Unable to load the MOU Word document from the database.'
        }
      } finally {
        this.previewLoading = false
      }
    },
    buildAgreementDraftAttachment() {
      const uploaded = this.selectedAgreement && this.selectedAgreement.uploadedDraft ? this.selectedAgreement.uploadedDraft : null
      if (uploaded && uploaded.url) {
        return {
          originalName: uploaded.name || this.selectedAgreement.fileLabel,
          fileName: uploaded.name || this.selectedAgreement.fileLabel,
          mimeType: uploaded.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          url: uploaded.url,
          fileUrl: uploaded.url
        }
      }
      const embedded = this.selectedAgreement && Array.isArray(this.selectedAgreement.attachments) ? this.selectedAgreement.attachments.find(item => item && item.url) : null
      if (embedded) {
        return {
          originalName: embedded.name || this.selectedAgreement.fileLabel,
          fileName: embedded.name || this.selectedAgreement.fileLabel,
          mimeType: embedded.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          url: embedded.url,
          fileUrl: embedded.url
        }
      }
      return null
    },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatShortDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    formatDateTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    selectComment(commentId) {
      this.activeCommentId = commentId
      if (!this.selectedAgreement) return
      const comments = Array.isArray(this.selectedAgreement.comments) ? this.selectedAgreement.comments : []
      const comment = comments.find(item => item && (item.id === commentId || item.commentId === commentId))
      if (comment && comment.articleKey) {
        this.focusCommitteeSection(comment.articleKey)
        return
      }
      const matchingSection = this.baseDraftPreviewSections.find(section => this.threadMatchesSection({ root: comment || {} }, section))
      if (matchingSection && matchingSection.key) this.focusCommitteeSection(matchingSection.key)
    },
    resolveCommentIdentifier(comment) {
      if (!(comment && typeof comment === 'object')) return ''
      return comment.commentId || comment.id || ''
    },
    prepareCommitteeReply(thread, comment = null) {
      if (!(thread && thread.root)) return
      const targetComment = comment && typeof comment === 'object' ? comment : thread.root
      const parentCommentId = this.resolveCommentIdentifier(targetComment)
      if (!parentCommentId) return
      this.showCommitteeComposer = true
      this.committeeForm.comments = ''
      this.clearCommitteeAnchor()
      this.replyTargetThreadId = thread.threadId
      this.replyTargetCommentId = parentCommentId
      this.committeeForm.articleKey = targetComment.articleKey || thread.root.articleKey || 'general'
      this.committeeForm.section = targetComment.section || targetComment.articleTitle || thread.root.section || thread.root.articleTitle || 'Committee recommendation'
      this.committeeForm.page = targetComment.page || thread.root.page || 1
      this.focusCommitteeSection(targetComment.articleKey || thread.root.articleKey || '')
      this.$nextTick(() => {
        const target = document.getElementById('committee-note')
        if (target) target.focus()
      })
    },
    clearCommitteeReplyTarget() {
      this.replyTargetThreadId = ''
      this.replyTargetCommentId = ''
    },
    async resolveCommitteeThread(thread) {
      if (!(thread && thread.threadId)) return
      const response = await api.committeeReview('resolve-comment', {
        id: this.selectedAgreement.agreementId || this.selectedAgreement.id,
        commentId: thread.threadId,
        status: 'resolved'
      })
      this.selectedAgreement = response && response.data && response.data.data ? response.data.data : this.selectedAgreement
      this.persistCommitteeDetailCache()
    },
    async undoResolveCommitteeThread(thread) {
      if (!(thread && thread.threadId)) return
      const response = await api.committeeReview('resolve-comment', {
        id: this.selectedAgreement.agreementId || this.selectedAgreement.id,
        commentId: thread.threadId,
        status: 'open'
      })
      this.selectedAgreement = response && response.data && response.data.data ? response.data.data : this.selectedAgreement
      this.persistCommitteeDetailCache()
    },
    chooseDecision(type) {
      this.selectedDecision = type
      if (type !== 'reject') {
        this.decisionNote = ''
      }
    },
    async ensureCommitteeStarted() {
      if (!this.selectedAgreement || !this.isCommittee) return
      if (Number(this.selectedAgreement.currentStep || 1) >= 3) return
      const response = await api.committeeReview('update', {
        id: this.selectedAgreement.agreementId || this.selectedAgreement.id,
        currentStep: 3
      })
      this.selectedAgreement = response && response.data && response.data.data ? response.data.data : this.selectedAgreement
      this.persistCommitteeDetailCache()
    },
    async saveCommitteeDraft() {
      if (!this.selectedAgreement || this.committeeCommentSubmitting) return
      const content = String(this.committeeForm.comments || '').trim()
      if (!content) {
        showWarningAlert(
          this.localeCode === 'th' ? 'กรุณาเพิ่มหมายเหตุของคณะกรรมการก่อนบันทึกร่าง' : 'Add a committee note before saving the draft.',
          this.localeCode === 'th' ? 'ต้องมีหมายเหตุ' : 'Note Required'
        )
        return
      }
      this.committeeCommentSubmitting = true
      try {
        await this.ensureCommitteeStarted()
        const response = await api.committeeReview('comment', {
          id: this.selectedAgreement.agreementId || this.selectedAgreement.id,
          content,
          articleKey: this.committeeForm.articleKey,
          articleTitle: this.currentCommitteeArticleTitle(),
          page: this.committeeForm.page,
          section: this.currentCommitteeArticleTitle(),
          threadId: this.replyTargetThreadId,
          parentId: this.replyTargetCommentId,
          anchor: this.committeeForm.anchor
        })
        this.selectedAgreement = response && response.data && response.data.data ? response.data.data : this.selectedAgreement
        this.persistCommitteeDetailCache()
        createWorkflowNotification({
          role: 'user',
          audienceKey: this.selectedAgreement && this.selectedAgreement.ownerEmail ? this.selectedAgreement.ownerEmail : '',
          type: 'review',
          title: this.localeCode === 'th' ? 'คณะกรรมการเพิ่มความคิดเห็นใหม่' : 'Committee added a new comment',
          message: this.localeCode === 'th'
            ? `${this.selectedAgreement && this.selectedAgreement.title ? this.selectedAgreement.title : 'ข้อตกลงของคุณ'} มีความคิดเห็นใหม่จากคณะกรรมการในเธรดการพิจารณา`
            : `${this.selectedAgreement && this.selectedAgreement.title ? this.selectedAgreement.title : 'Your agreement'} has a new committee comment in the live review thread.`,
          source: this.localeCode === 'th' ? 'คิวตรวจทานของฉัน' : 'My Review Queue',
          partnerName: this.selectedAgreement && this.selectedAgreement.partner ? this.selectedAgreement.partner : '',
          reference: this.selectedAgreement ? `MOU-${this.selectedAgreement.agreementId || this.selectedAgreement.id}` : '',
          priority: 'medium',
          targetPath: this.selectedAgreement ? `/dashboard/user-agreements/${this.selectedAgreement.agreementId || this.selectedAgreement.id}/start-review` : '/dashboard/user-review-queue',
          actionLabel: this.localeCode === 'th' ? 'เปิดความคิดเห็น' : 'Open comments',
          nextStep: this.localeCode === 'th' ? 'อ่านความคิดเห็นใหม่จากคณะกรรมการและตอบกลับหรือแก้ไขร่าง' : 'Read the new committee comment and reply or revise the draft.'
        })
        this.committeeForm.comments = ''
        this.committeeForm.anchor = null
        this.committeeForm.page = 1
        this.committeeForm.articleKey = this.articleOptions.length ? this.articleOptions[0].key : 'general'
        this.committeeForm.section = this.localeCode === 'th' ? 'ข้อเสนอแนะของคณะกรรมการ' : 'Committee recommendation'
        this.showCommitteeComposer = false
        this.clearCommitteeReplyTarget()
        this.activeCommentId = this.selectedAgreement && this.selectedAgreement.comments && this.selectedAgreement.comments.length
          ? this.selectedAgreement.comments[this.selectedAgreement.comments.length - 1].id
          : ''
      } finally {
        this.committeeCommentSubmitting = false
      }
    },
    async submitCommitteeReview() {
      if (!this.selectedAgreement) return
      if (!this.commentThreads.length) {
        showWarningAlert(this.text.submitLockedNoComments, this.text.submitLockedTitle)
        return
      }
      if (this.unresolvedCommentThreadCount > 0) {
        showWarningAlert(this.text.submitLockedUnresolved, this.text.submitLockedTitle)
        return
      }
      const response = await api.committeeReview('submit', {
        id: this.selectedAgreement.agreementId || this.selectedAgreement.id,
        decision: 'approve',
        content: '',
        articleKey: this.committeeForm.articleKey,
        articleTitle: this.currentCommitteeArticleTitle(),
        page: this.committeeForm.page,
        section: this.committeeForm.section
      })
      this.selectedAgreement = response && response.data && response.data.data ? response.data.data : this.selectedAgreement
      this.persistCommitteeDetailCache()
      const approvalProgress = this.selectedAgreement && this.selectedAgreement.committeeApprovalProgress
        ? this.selectedAgreement.committeeApprovalProgress
        : { pendingCount: 0, pendingReviewers: [] }
      const readyForAdminFinalApproval = !!(this.selectedAgreement && this.selectedAgreement.readyForAdminFinalApproval)
      const targetAgreementId = this.selectedAgreement ? (this.selectedAgreement.agreementId || this.selectedAgreement.id) : ''
      if (readyForAdminFinalApproval) {
        createWorkflowNotification({
          role: 'admin',
          type: 'approval',
          title: this.localeCode === 'th' ? 'คณะกรรมการตรวจพิจารณาเสร็จสิ้น' : 'Committee review completed',
          message: this.localeCode === 'th'
            ? `${this.selectedAgreement && this.selectedAgreement.title ? this.selectedAgreement.title : 'มีข้อตกลงหนึ่งรายการ'} พร้อมสำหรับการตัดสินใจขั้นสุดท้ายของผู้ดูแล`
            : `${this.selectedAgreement && this.selectedAgreement.title ? this.selectedAgreement.title : 'An agreement'} is ready for the final admin decision.`,
          source: this.localeCode === 'th' ? 'คิวตรวจพิจารณา' : 'Review Queue',
          partnerName: this.selectedAgreement && this.selectedAgreement.partner ? this.selectedAgreement.partner : '',
          reference: targetAgreementId ? `MOU-${targetAgreementId}` : '',
          priority: 'high',
          targetPath: targetAgreementId ? `/review-queue/${targetAgreementId}/final-approval` : '/review-queue',
          actionLabel: this.localeCode === 'th' ? 'เปิดการอนุมัติขั้นสุดท้าย' : 'Open final approval',
          nextStep: this.localeCode === 'th' ? 'อนุมัติหรือปฏิเสธข้อตกลง' : 'Approve or reject the agreement.'
        })
        await showSuccessAlert(
          this.localeCode === 'th'
            ? 'บันทึกการอนุมัติครบทุกคนแล้ว ข้อตกลงพร้อมสำหรับการตัดสินใจขั้นสุดท้ายของผู้ดูแล'
            : 'All committee approvals are now complete. The agreement is ready for the final admin decision.',
          this.localeCode === 'th' ? 'พร้อมส่งให้ผู้ดูแล' : 'Ready For Admin'
        )
      } else {
        await showSuccessAlert(
          this.localeCode === 'th'
            ? 'บันทึกการอนุมัติของคุณแล้ว'
            : 'Your approval was recorded.',
          this.localeCode === 'th' ? 'บันทึกการอนุมัติแล้ว' : 'Approval Recorded'
        )
      }
      this.committeeForm = {
        articleKey: this.articleOptions.length ? this.articleOptions[0].key : 'general',
        decision: 'approve',
        comments: '',
        finalSummary: '',
        page: 1,
        section: this.localeCode === 'th' ? 'ข้อเสนอแนะของคณะกรรมการ' : 'Committee recommendation'
      }
      this.$router.push('/review')
    },
    async previousStep() {
      if (!this.selectedAgreement || this.currentStep <= 1) return
      const response = await api.adminReview('update', {
        id: this.selectedAgreement.id,
        currentStep: Math.max(this.currentStep - 1, 1),
        status: Math.max(this.currentStep - 1, 1) >= 2 ? 'pending' : 'draft',
        assignedReviewerIds: [...this.draftReviewerIds]
      })
      const nextItem = response && response.data && response.data.data ? response.data.data : null
      this.selectedAgreement = nextItem
      this.activeCommentId = nextItem && nextItem.comments && nextItem.comments.length ? nextItem.comments[0].id : ''
    },
    async nextStep() {
      if (!this.selectedAgreement) return

      const nextStep = Math.min(this.currentStep + 1, 4)
      const response = await api.adminReview('update', {
        id: this.selectedAgreement.id,
        assignedReviewerIds: [...this.draftReviewerIds],
        currentStep: nextStep,
        status: nextStep >= 2 ? 'pending' : 'draft'
      })
      const nextItem = response && response.data && response.data.data ? response.data.data : null
      this.selectedAgreement = nextItem
    },
    async approveAgreement() {
      if (!this.selectedAgreement) return
      const response = await api.adminReview('decision', {
        id: this.selectedAgreement.id,
        decision: 'approve',
        decisionNote: this.decisionNote
      })
      const nextItem = response && response.data && response.data.data ? response.data.data : null
      this.selectedAgreement = nextItem
      if (nextItem) {
        createWorkflowNotification({
          role: 'user',
          audienceKey: nextItem.ownerEmail || '',
          type: 'approval',
          title: this.localeCode === 'th' ? 'ผู้ดูแลอนุมัติข้อตกลงแล้ว' : 'Agreement approved by admin',
          message: this.localeCode === 'th'
            ? `${nextItem.title || 'ข้อตกลงของคุณ'} ได้รับการอนุมัติจากผู้ดูแลแล้ว`
            : `${nextItem.title || 'Your agreement'} was approved by admin.`,
          source: this.localeCode === 'th' ? 'ข้อตกลงของฉัน' : 'My Agreements',
          partnerName: nextItem.partner || '',
          reference: `MOU-${nextItem.id}`,
          priority: 'high',
          targetPath: `/dashboard/user-agreements/${nextItem.id}/review-summary?mode=view`,
          actionLabel: this.localeCode === 'th' ? 'ดูข้อตกลงที่อนุมัติแล้ว' : 'View approved agreement',
          nextStep: this.localeCode === 'th' ? 'เปิดดูข้อตกลงที่อนุมัติแล้วและตรวจสอบสถานะสุดท้าย' : 'Open the approved agreement and review the final status.'
        })
        createWorkflowNotification({
          role: 'committee',
          type: 'approval',
          title: this.localeCode === 'th' ? 'ผู้ดูแลอนุมัติข้อตกลงแล้ว' : 'Agreement approved by admin',
          message: this.localeCode === 'th'
            ? `${nextItem.title || 'มีข้อตกลงหนึ่งรายการ'} ได้รับการอนุมัติจากผู้ดูแลแล้ว`
            : `${nextItem.title || 'An agreement'} was approved by admin.`,
          source: this.localeCode === 'th' ? 'ประวัติการพิจารณา' : 'Review History',
          partnerName: nextItem.partner || '',
          reference: `MOU-${nextItem.id}`,
          priority: 'medium',
          targetPath: '/review-history',
          actionLabel: this.localeCode === 'th' ? 'เปิดประวัติการพิจารณา' : 'Open review history',
          nextStep: this.localeCode === 'th' ? 'เปิดประวัติการพิจารณาและตรวจสอบผลที่อนุมัติแล้ว' : 'Open review history and check the final approved result.'
        })
      }
      this.$router.push('/agreements')
    },
    async rejectAgreement() {
      if (!this.selectedAgreement) return
      const response = await api.adminReview('decision', {
        id: this.selectedAgreement.id,
        decision: 'reject',
        decisionNote: this.decisionNote
      })
      const nextItem = response && response.data && response.data.data ? response.data.data : null
      this.selectedAgreement = nextItem
      if (nextItem) {
        createWorkflowNotification({
          role: 'user',
          audienceKey: nextItem.ownerEmail || '',
          type: 'review',
          title: this.localeCode === 'th' ? 'ผู้ดูแลส่งข้อตกลงกลับแล้ว' : 'Agreement returned by admin',
          message: this.localeCode === 'th'
            ? `${nextItem.title || 'ข้อตกลงของคุณ'} ถูกส่งกลับโดยผู้ดูแลและต้องแก้ไขเพิ่มเติม`
            : `${nextItem.title || 'Your agreement'} was returned by admin and needs further updates.`,
          source: this.localeCode === 'th' ? 'คิวตรวจทานของฉัน' : 'My Review Queue',
          partnerName: nextItem.partner || '',
          reference: `MOU-${nextItem.id}`,
          priority: 'high',
          targetPath: `/dashboard/user-agreements/${nextItem.id}/review-summary?mode=view`,
          actionLabel: this.localeCode === 'th' ? 'เปิดข้อตกลง' : 'Open agreement',
          nextStep: this.localeCode === 'th' ? 'ตรวจสอบคำตัดสินของผู้ดูแลและดำเนินขั้นตอนต่อ' : 'Review the admin decision and continue the workflow.'
        })
        createWorkflowNotification({
          role: 'committee',
          dedupeKey: `committee-admin-reject-${nextItem.id}-${Date.now()}`,
          type: 'review',
          title: this.localeCode === 'th' ? 'ผู้ดูแลส่งข้อตกลงกลับแล้ว' : 'Agreement returned by admin',
          message: this.localeCode === 'th'
            ? `${nextItem.title || 'มีข้อตกลงหนึ่งรายการ'} ถูกส่งกลับโดยผู้ดูแลและต้องมีการดำเนินการต่อ`
            : `${nextItem.title || 'An agreement'} was returned by admin and needs follow-up from the committee workflow.`,
          source: this.localeCode === 'th' ? 'ประวัติการพิจารณา' : 'Review History',
          partnerName: nextItem.partner || '',
          reference: `MOU-${nextItem.id}`,
          priority: 'medium',
          targetPath: '/review-history',
          actionLabel: this.localeCode === 'th' ? 'เปิดประวัติการพิจารณา' : 'Open review history',
          nextStep: this.localeCode === 'th' ? 'เปิดประวัติการพิจารณาและตรวจสอบคำตัดสินของผู้ดูแล' : 'Open review history and review the admin decision.'
        })
      }
      this.draftReviewerIds = []
      this.$router.push('/review-queue')
    },
    submitDecision() {
      if (this.selectedDecision === 'approve') {
        this.approveAgreement()
        return
      }
      if (this.selectedDecision === 'reject') {
        if (!this.decisionNote.trim()) {
          showWarningAlert(
            this.localeCode === 'th' ? 'กรุณาระบุเหตุผลในการปฏิเสธก่อนส่ง' : 'Please provide the reason for rejection before submitting.',
            this.localeCode === 'th' ? 'ต้องระบุเหตุผล' : 'Reason Required'
          )
          return
        }
        this.rejectAgreement()
        return
      }
      showWarningAlert(
        this.localeCode === 'th' ? 'กรุณาเลือกอนุมัติหรือปฏิเสธก่อน' : 'Please choose approve or reject first.',
        this.localeCode === 'th' ? 'ต้องเลือกผลการตัดสินใจ' : 'Decision Required'
      )
    },
    goBack() {
      this.$router.push(this.isCommittee ? '/review' : '/review-queue')
    },
    currentCommitteeArticleTitle() {
      const selected = this.articleOptions.find(item => item.key === this.committeeForm.articleKey)
      return selected ? selected.title : this.text.general
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("review-page");
@include workspace-toolbar-search;
@include workspace-button-system;

.review-page {
  padding: 24px;
}

.review-shell {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.review-header,
.progress-card,
.stepper-card {
  background: #fff;
  border: 1px solid #e9edf3;
  border-radius: 18px;
}

.review-header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
}

.eyebrow {
  color: #9e212f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.review-header h1 {
  color: #2d3748;
  font-size: 30px;
  font-weight: 700;
  margin: 0 0 6px;
}

.review-header p,
.section-subtitle,
.comment-role,
.comment-head span,
.info-copy,
.empty-state p {
  color: #718096;
}

.header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.meta-chip {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
}

.meta-chip--status {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
}

.committee-stage-switch {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 12px;
}

.committee-stage-switch__btn {
  background: #f8fafc;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  color: #4a5568;
  font-size: 0.95rem;
  font-weight: 700;
  padding: 10px 18px;
}

.committee-stage-switch__btn--active {
  background: #8c1515;
  border-color: #8c1515;
  color: #fff;
}

.progress-card {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: 220px 1fr 70px;
  padding: 20px 24px;
}

.progress-label {
  color: #718096;
  font-size: 13px;
}

.progress-stage,
.section-title,
.panel-title,
.info-title,
.empty-state h2 {
  color: #2d3748;
  font-weight: 700;
}

.progress-track {
  background: #edf2f7;
  border-radius: 999px;
  height: 12px;
  overflow: hidden;
}

.progress-fill {
  background: linear-gradient(90deg, #9e212f 0%, #f5a623 100%);
  border-radius: 999px;
  height: 100%;
}

.progress-value {
  color: #9e212f;
  font-size: 18px;
  font-weight: 700;
  text-align: right;
}

.stepper-card {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 16px;
}

.step-pill {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  display: flex;
  gap: 10px;
  padding: 14px;
}

.step-pill--active {
  background: #fff7ed;
  border-color: #f5a623;
}

.step-pill--done {
  background: #fef2f2;
  border-color: #fecaca;
}

.step-number {
  align-items: center;
  background: #fff;
  border-radius: 999px;
  color: #9e212f;
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.step-text {
  color: #4a5568;
  font-size: 14px;
  font-weight: 600;
}

.review-card {
  border-radius: 18px;
}

.review-card--full {
  width: 100%;
}

.committee-landing-grid {
  display: grid;
  gap: 16px;
}

.committee-landing-card {
  background: #fafafa;
  border: 1px solid #edf2f7;
  border-radius: 16px;
  padding: 18px;
}

.committee-landing-card__head,
.committee-landing-card__badges {
  display: flex;
  gap: 10px;
}

.committee-landing-card__head {
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
}

.committee-landing-card__title {
  color: #2d3748;
  font-size: 1.05rem;
  font-weight: 700;
}

.committee-landing-card__meta {
  color: #718096;
  margin-top: 4px;
}

.filter-shell,
.table-shell {
  border-radius: var(--app-radius-xl);
  margin-bottom: 16px;
}

.table-head {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.table-head__copy,
.history-meta,
.history-date,
.history-time,
.history-note {
  color: #718096;
}

.history-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.history-table {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 18px;
  overflow: hidden;
  min-width: 1040px;
}

.history-table__header,
.history-table__row {
  align-items: center;
  column-gap: 20px;
  display: grid;
  grid-template-columns: minmax(220px, 1.65fr) minmax(120px, 0.85fr) minmax(170px, 1fr) minmax(130px, 0.85fr) minmax(145px, 0.9fr) 108px;
  padding: 18px 20px;
}

.history-table__header {
  background: #f8fafc;
  border-bottom: 1px solid #e6ebf1;
  color: #52606d;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.history-table__row {
  border-bottom: 1px solid #eef1f6;
}

.history-table__row:last-child {
  border-bottom: 0;
}

.history-table__cell {
  min-width: 0;
}

.history-table__cell--date {
  display: grid;
  gap: 4px;
}

.history-mobile-label {
  display: none;
}

.history-table__cell--actions {
  display: flex;
  justify-content: center;
}

.history-title {
  color: #344767;
  font-weight: 700;
}

.history-value {
  color: #344767;
  display: inline-block;
  line-height: 1.5;
}

.history-note {
  font-size: 0.88rem;
  line-height: 1.5;
  margin-top: 6px;
}

.decision-pill {
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  justify-content: center;
  line-height: 1.35;
  min-width: 112px;
  padding: 8px 12px;
  text-align: center;
  white-space: normal;
}

.decision-pill--changes-requested {
  background: #fff4df;
  color: #b54708;
}

.decision-pill--approved {
  background: #ecfdf3;
  color: #027a48;
}

.decision-pill--sent-to-admin {
  background: #eff8ff;
  color: #175cd3;
}

.decision-pill--rejected {
  background: #fff1f3;
  color: #b42318;
}

.empty-state {
  padding: 32px 8px;
  text-align: center;
}

.empty-state__title {
  color: #344767;
  font-weight: 700;
  margin-bottom: 6px;
}

.empty-state__copy {
  color: #718096;
}

.history-action-btn {
  background: linear-gradient(180deg, #ffffff 0%, #edf2f7 100%);
  border: 1px solid #d7e1ec;
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
  color: #344767;
  font-weight: 800;
  min-width: 72px;
}

.history-action-btn:hover,
.history-action-btn:focus {
  background: linear-gradient(180deg, #ffffff 0%, #edf2f7 100%);
  border-color: #c36b6b;
  box-shadow: 0 0 0 4px rgba(140, 21, 21, 0.08);
  color: #344767;
}

.history-date--urgent {
  color: #b42318;
}

.empty-assignment--landing {
  background: #fafafa;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  padding: 28px;
  text-align: center;
}

.card-heading {
  margin-bottom: 18px;
}

.step-page,
.preview-layout,
.full-page-layout,
.decision-layout,
.committee-layout {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1.25fr) minmax(320px, 0.75fr);
  align-items: start;
}


.committee-main,
.committee-side,
.committee-comments,
.form-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.committee-comments--thread {
  max-height: 560px;
  overflow-y: auto;
  padding-right: 4px;
}

.step-page--intro {
  min-height: 440px;
}

.hero-block {
  background: linear-gradient(135deg, #fff7ed 0%, #fff 100%);
  border: 1px solid #fde7c2;
  border-radius: 18px;
  padding: 20px;
}

.info-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
}

.detail-grid {
  display: grid;
  gap: 12px;
}

.detail-grid--wide {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.detail-grid.compact .detail-row {
  padding: 10px 0;
}

.detail-row {
  align-items: flex-start;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 0;
}

.detail-row span {
  color: #718096;
}

.detail-row strong {
  color: #2d3748;
  max-width: 62%;
  text-align: right;
}

.detail-row--wide {
  grid-column: 1 / -1;
}

.full-page-layout,
.decision-layout,
.committee-layout {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.6fr) minmax(320px, 0.9fr);
}

.committee-layout--synced {
  grid-template-columns: minmax(0, 1.25fr) minmax(360px, 0.9fr);
}

.reviewer-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.page-side .reviewer-grid {
  grid-template-columns: 1fr;
}

.reviewer-option {
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  padding: 16px;
}

.page-side .reviewer-option {
  align-items: flex-start;
  min-width: 0;
  width: 100%;
}

.page-side .reviewer-option input {
  flex-shrink: 0;
  margin-top: 4px;
}

.page-side .reviewer-copy {
  min-width: 0;
}

.reviewer-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reviewer-copy strong {
  color: #2d3748;
}

.reviewer-copy span,
.reviewer-copy small,
.empty-assignment {
  color: #718096;
}

.preview-layout {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.95fr);
}

.preview-layout--full {
  min-height: 620px;
}

.preview-panel,
.comment-panel,
.summary-panel {
  background: #fafafa;
  border: 1px solid #edf2f7;
  border-radius: 16px;
  padding: 16px;
}

.assignment-banner {
  align-items: flex-start;
  background: #fffaf0;
  border: 1px solid #f6d9a6;
  border-radius: 18px;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 18px 20px;
}

.assignment-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.assignment-tag {
  background: #fff;
  border: 1px solid #f3c97f;
  border-radius: 999px;
  color: #9a3412;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 12px;
}

.assignment-tag--approved {
  background: #ecfdf3;
  border-color: #bbf7d0;
  color: #166534;
}

.assigned-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.assigned-person {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
}

.assigned-person strong {
  color: #2d3748;
}

.assigned-person span {
  color: #718096;
}

.committee-submit-summary {
  margin: 14px 0 6px;
}

.committee-complete-review {
  margin-top: 22px;
}

.committee-complete-review--blocked {
  background: #fffaf0;
  border-color: #f6d9a6;
}

.version-panel {
  margin-top: 16px;
}

.version-panel--compact {
  margin-top: 0;
  padding: 14px 16px;
}

.version-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.version-list--compact {
  gap: 8px;
  margin-top: 10px;
}

.version-item {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: grid;
  gap: 4px;
  padding: 12px 14px;
}

.version-item strong {
  color: #2d3748;
}

.version-item span {
  color: #718096;
}

.version-item--compact {
  gap: 2px;
  padding: 10px 12px;
}

.version-item--compact span {
  font-size: 0.9rem;
}

.panel-title--sub {
  font-size: 0.95rem;
  margin-top: 4px;
}

.live-history-divider {
  border-top: 1px solid #e2e8f0;
  margin: 16px 0;
}

.live-history-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.live-history-item {
  align-items: flex-start;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  display: grid;
  gap: 10px;
  grid-template-columns: 10px minmax(0, 1fr);
  padding: 12px;
}

.live-history-dot {
  background: #9e212f;
  border-radius: 999px;
  box-shadow: 0 0 0 4px rgba(158, 33, 47, 0.1);
  height: 10px;
  margin-top: 5px;
  width: 10px;
}

.live-history-item--version .live-history-dot {
  background: #175cd3;
  box-shadow: 0 0 0 4px rgba(23, 92, 211, 0.1);
}

.live-history-copy {
  min-width: 0;
}

.live-history-head {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.live-history-head strong {
  color: #2d3748;
  font-size: 0.94rem;
}

.live-history-head span,
.live-history-meta,
.live-history-summary {
  color: #718096;
}

.live-history-head span {
  flex-shrink: 0;
  font-size: 0.78rem;
  text-align: right;
}

.live-history-meta {
  font-size: 0.82rem;
  line-height: 1.45;
  margin-top: 2px;
}

.live-history-summary {
  font-size: 0.9rem;
  line-height: 1.5;
  margin-top: 6px;
  overflow-wrap: anywhere;
}

.decision-side--stacked {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.pdf-sheet {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  min-height: 520px;
  padding: 24px;
}

.pdf-sheet__branding {
  border-bottom: 1px solid #eef2f7;
  margin: 0 0 18px;
  padding-bottom: 18px;
}

.pdf-topline {
  color: #718096;
  display: flex;
  font-size: 13px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.pdf-reference {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  color: #9a3412;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 16px;
  padding: 10px 12px;
}

.pdf-frame-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.pdf-frame {
  background: #f8fafc;
  border: 0;
  display: block;
  height: 720px;
  width: 100%;
}

.draft-preview-stack {
  display: grid;
  gap: 12px;
}

.draft-preview-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  position: relative;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.draft-preview-card__header {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.draft-preview-card__title {
  color: #2d3748;
  font-weight: 700;
  margin-bottom: 8px;
}

.draft-preview-card__copy {
  color: #4a5568;
  line-height: 1.6;
  white-space: pre-wrap;
}

.comment-anchor-highlight {
  background: #fde68a;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.18);
  color: #713f12;
  font-weight: 800;
  padding: 1px 3px;
}

.comment-anchor-highlight--resolved {
  background: #bbf7d0;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.16);
  color: #14532d;
}

.draft-preview-card--focus {
  border-color: #f59e0b;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.22), 0 18px 32px rgba(146, 64, 14, 0.12);
}

.draft-preview-card--has-change {
  background: #fffaf0;
  border-color: #f8c15c;
}

.draft-preview-card--open-change {
  background: #fff7ed;
  border-color: #f97316;
  box-shadow: inset 5px 0 0 #f97316;
}

.draft-preview-card--focus.draft-preview-card--open-change {
  box-shadow: inset 5px 0 0 #f97316, 0 0 0 4px rgba(245, 158, 11, 0.22), 0 18px 32px rgba(146, 64, 14, 0.12);
}

.draft-preview-card__badge {
  background: #9e212f;
  border-radius: 999px;
  color: #fff;
  flex: 0 0 auto;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1;
  padding: 7px 10px;
  text-transform: uppercase;
}

.draft-preview-card__markers {
  display: grid;
  gap: 8px;
  margin: 4px 0 12px;
}

.draft-preview-card__marker {
  background: #fff;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: #7c2d12;
  cursor: pointer;
  display: block;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.35;
  max-width: 100%;
  overflow: hidden;
  padding: 9px 11px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draft-preview-card__marker:hover,
.draft-preview-card__marker:focus {
  border-color: #f97316;
  box-shadow: 0 8px 18px rgba(249, 115, 22, 0.12);
  outline: none;
}

.draft-preview-card__marker--resolved {
  border-color: #bbf7d0;
  color: #166534;
}

.pdf-empty-state {
  align-items: center;
  color: #718096;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 420px;
  text-align: center;
}

.pdf-empty-state strong {
  color: #2d3748;
}

.comment-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  margin-top: 12px;
  padding: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.comment-card--interactive:hover,
.comment-card--interactive:focus {
  border-color: #d5deea;
  box-shadow: 0 10px 24px rgba(44, 52, 71, 0.08);
  outline: none;
  transform: translateY(-1px);
}

.comment-card--committee {
  background: #fff5f5;
  border-color: #fed7d7;
}

.comment-card--user {
  background: #f7fafc;
  border-color: #e2e8f0;
}

.comment-card--unresolved {
  background: #fff5f5;
  border-color: #fecaca;
}

.comment-card--resolved {
  background: #f0fdf4;
  border-color: #86efac;
}

.comment-card:first-of-type {
  margin-top: 16px;
}

.comment-card--active {
  border-color: #f5a623;
  box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.18);
}

.comment-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.comment-copy {
  color: #4a5568;
  margin-top: 8px;
  white-space: pre-wrap;
}

.comment-change-target {
  align-items: flex-start;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: #7c2d12;
  display: grid;
  gap: 2px;
  margin-top: 10px;
  padding: 9px 11px;
}

.comment-change-target span {
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.comment-change-target strong {
  color: #344767;
}

.comment-change-target em {
  background: rgba(255, 255, 255, 0.76);
  border-left: 3px solid currentColor;
  border-radius: 6px;
  display: block;
  font-style: normal;
  font-weight: 800;
  margin-top: 5px;
  padding: 6px 8px;
}

.comment-change-target--resolved {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.selected-anchor-chip {
  align-items: center;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  color: #7c2d12;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 2px;
  padding: 8px 10px;
}

.selected-anchor-chip span {
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.selected-anchor-chip strong {
  color: #344767;
}

.selected-anchor-chip button {
  background: transparent;
  border: 0;
  color: #9e212f;
  font-weight: 800;
  margin-left: auto;
  padding: 0;
}

.comment-thread-link--undo {
  color: #15803d;
}

.inline-reply-indicator {
  align-items: center;
  color: #8c1515;
  display: flex;
  gap: 8px;
  font-size: 0.92rem;
  font-weight: 700;
  margin: 8px 4px 0;
}

.inline-reply-indicator__arrow {
  font-size: 1rem;
  line-height: 1;
}

.composer-launch {
  margin-top: 14px;
}

.composer-launch__btn {
  background: linear-gradient(135deg, #9e212f 0%, #8a1728 100%);
  border: 1px solid #9e212f;
  border-radius: 999px;
  color: #fff;
  cursor: pointer;
  font-weight: 700;
  padding: 10px 16px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease;
}

.composer-launch__btn:hover,
.composer-launch__btn:focus {
  background: linear-gradient(135deg, #8a1728 0%, #721220 100%);
  border-color: #8a1728;
  box-shadow: 0 10px 24px rgba(158, 33, 47, 0.2);
  outline: none;
  transform: translateY(-1px);
}

.form-stack--composer {
  background: #fffaf5;
  border: 1px solid #fed7aa;
  border-radius: 16px;
  margin-top: 16px;
  padding: 16px;
}

.form-stack--inline {
  margin-top: 10px;
}

.form-stack--final-simple {
  margin-top: 16px;
}

.decision-actions--stacked {
  align-items: flex-start;
  flex-direction: column;
}


.article-reference {
  background: #fff;
  border: 1px solid #fde7c2;
  border-radius: 12px;
  color: #9a3412;
  font-size: 0.92rem;
  font-weight: 600;
  padding: 10px 12px;
}

.comment-thread-shell + .comment-thread-shell {
  margin-top: 12px;
}

.comment-thread-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.comment-thread-link {
  background: none;
  border: 0;
  color: #9e212f;
  cursor: pointer;
  font-weight: 700;
  padding: 0;
}

.comment-thread-link:hover,
.comment-thread-link:focus {
  color: #8a1728;
  outline: none;
  text-decoration: underline;
}

.comment-thread-state {
  color: #15803d;
  font-size: 0.9rem;
  font-weight: 700;
}

.comment-thread-replies {
  border-left: 2px solid #e5e7eb;
  margin-left: 18px;
  padding-left: 14px;
}

.comment-card--reply {
  margin-top: 10px;
}

.reply-target-banner {
  color: #8c1515;
  font-size: 0.92rem;
  font-weight: 600;
  margin: 4px 0 2px;
}

.panel-title--composer {
  margin-top: 8px;
}

.decision-label {
  color: #4a5568;
  display: block;
  font-weight: 600;
  margin-top: 4px;
  margin-bottom: 8px;
}

.decision-note {
  border: 1px solid #d9e2ec;
  border-radius: 14px;
  padding: 14px;
  resize: vertical;
  width: 100%;
}

.decision-select,
.decision-input {
  border: 1px solid #d9e2ec;
  border-radius: 14px;
  margin-top: 8px;
  padding: 12px 14px;
  width: 100%;
}

.decision-choice-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 14px;
}

.decision-choice {
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1.5px solid #d7dee8;
  border-radius: 16px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  min-height: 86px;
  padding: 14px 16px;
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease;
}

.decision-choice strong {
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 700;
}

.decision-choice:hover {
  border-color: #b8c4d3;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.decision-choice--approve.decision-choice--selected {
  background: linear-gradient(180deg, #f3fff7 0%, #e7f8ee 100%);
  border-color: #38a169;
  box-shadow: 0 0 0 3px rgba(56, 161, 105, 0.12);
}

.decision-choice--reject.decision-choice--selected {
  background: linear-gradient(180deg, #fff7f7 0%, #fdeeee 100%);
  border-color: #d64545;
  box-shadow: 0 0 0 3px rgba(214, 69, 69, 0.12);
}

.reject-review-box {
  margin-top: 20px;
}

.decision-actions,
.footer-actions {
  display: flex;
  gap: 12px;
  margin-top: 18px;
}

.footer-actions--spread {
  align-items: center;
  justify-content: space-between;
}

.footer-actions__left {
  display: flex;
  gap: 12px;
}

.empty-state--page {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
}

@media (max-width: 1199px) {
  .full-page-layout,
  .preview-layout,
  .decision-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 991px) {
  .toolbar,
  .toolbar-filters,
  .table-head {
    align-items: stretch;
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .history-table__header,
  .history-table__row {
    grid-template-columns: minmax(180px, 1.5fr) minmax(105px, 0.78fr) minmax(130px, 0.95fr) minmax(115px, 0.8fr) minmax(120px, 0.82fr) 96px;
  }

  .progress-card {
    grid-template-columns: 1fr;
  }

  .stepper-card {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .review-page {
    padding: 16px;
  }

  .review-header {
    flex-direction: column;
  }

  .committee-grid,
  .reviewer-grid,
  .decision-choice-grid,
  .stepper-card {
    grid-template-columns: 1fr;
  }

  .history-table__header {
    display: none;
  }

  .history-table {
    min-width: 0;
  }

  .history-table__row {
    align-items: flex-start;
    grid-template-columns: 1fr;
    row-gap: 10px;
  }

  .history-mobile-label {
    color: #52606d;
    display: block;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .history-table__cell--actions {
    justify-content: flex-start;
  }

  .detail-grid--wide,
  .assignment-banner,
  .footer-actions,
  .decision-actions,
  .footer-actions__left {
    flex-direction: column;
  }

  .footer-actions--spread {
    align-items: stretch;
  }
}
</style>
